import { useEffect, useMemo, useState, useRef, createContext, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/roleLabels";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Trash2, Pencil, ChevronUp, ChevronDown, X,
  Search, Upload, Image as ImageIcon, ExternalLink, Eye, AlertTriangle,
  BookOpen, Zap, Package, Images, Users, MessagesSquare, BarChart3, Settings2, PanelLeftClose, PanelLeftOpen, Link2, Layers3, FileText, MessageCircleMore, ClipboardList, KeyRound,
} from "lucide-react";
import RichEditor from "@/components/admin/RichEditor";
import NotificationBell from "@/components/NotificationBell";
import MOTestingAdmin from "@/components/admin/MOTestingAdmin";
import { MessageCircle } from "lucide-react";

function EditorialTabRedirect() {
  const nav = useNavigate();
  useEffect(() => { nav("/admin/editorial", { replace: true }); }, [nav]);
  return null;
}

// Cross-panel navigation: any panel can request a tab switch and pre-select an
// entity (product for Structure, material for Materials editor). Overview
// Quick Actions also use this to open the create dialog in the target panel.
const AdminNavCtx = createContext({
  goto: (_tab, _opts) => {},
  pendingProductId: "",
  clearPendingProductId: () => {},
  pendingMaterialId: "",
  clearPendingMaterialId: () => {},
  pendingInstructionId: "",
  clearPendingInstructionId: () => {},
  pendingAction: null,
  clearPendingAction: () => {},
  pendingFilter: null,
  clearPendingFilter: () => {},
  role: "user",
});
const useAdminNav = () => useContext(AdminNavCtx);

// Owner/Administrator use the existing operational workspace. Curator gets only scoped work.
const ALL_SECTIONS = [
  { id: "overview",     label: "Обзор" },
  { id: "products",     label: "Продукты" },
  { id: "editorial",    label: "Редакция" },
  { id: "structure",    label: "Структура" },
  { id: "sections",     label: "Разделы" },
  { id: "materials",    label: "Материалы" },
  { id: "media",        label: "Медиатека" },
  { id: "relations",    label: "Связи" },
  { id: "support",      label: "Обращения" },
  { id: "comments",     label: "Комментарии" },
  { id: "analytics",    label: "Аналитика" },
  { id: "users",        label: "Пользователи",  roles: ["owner", "administrator"] },
  { id: "access",       label: "Доступы",       roles: ["owner", "administrator"] },
  { id: "mo_testing",   label: "Отзывы о Мастере заказа", roles: ["owner", "administrator"] },
  { id: "instructions", label: "Инструкции" },
];
const sectionsForRole = (role) =>
  role === "curator"
    ? ALL_SECTIONS.filter((s) => ["support", "comments", "analytics"].includes(s.id))
    : ALL_SECTIONS.filter((s) => !s.roles || s.roles.includes(role));

// Owner-facing media types (backend accepts free-form string).
const MEDIA_TYPES = [
  ["photo",        "Фотография"],
  ["cover",        "Обложка"],
  ["illustration", "Иллюстрация"],
  ["diagram",      "Схема / диаграмма"],
  ["ingredient",   "Ингредиент"],
  ["dessert",      "Готовый десерт"],
  ["process",      "Процесс"],
  ["author",       "Автор / портрет"],
  ["other",        "Другое"],
];
const MEDIA_TYPE_LABEL = Object.fromEntries(MEDIA_TYPES);

// Human labels for media usage locations.
const USAGE_KIND_LABEL = {
  product:        "Обложка продукта",
  material_cover: "Обложка материала",
  material_body:  "Тело материала",
  section:        "Обложка раздела",
};

// Instruction categories mirror the backend list.
const INSTRUCTION_CATEGORIES = [
  "Начало работы",
  "Редакция",
  "Медиатека",
  "Материалы",
  "Продукты и структура",
  "Правила названий",
  "Словарь тегов",
  "Ошибки и решения",
];

const STATUS_OPTIONS = [
  ["draft",     "Черновик"],
  ["review",    "На проверке"],
  ["published", "Опубликовано"],
  ["hidden",    "Скрыто"],
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS);

// Owner-visible material types (per sprint spec). Backend still supports GUIDE/REFERENCE.
const MATERIAL_TYPES = [
  ["ARTICLE",     "Статья"],
  ["RECIPE",      "Рецепт"],
  ["FORMULATION", "Рецептура"],
];
const MATERIAL_TYPE_LABEL = {
  ARTICLE: "Статья", RECIPE: "Рецепт", FORMULATION: "Рецептура",
  GUIDE: "Гайд", REFERENCE: "Справочник",
};

const PRODUCT_TYPES = [
  ["VOLUME",         "Том"],
  ["COURSE",         "Курс"],
  ["WORKSHOP",       "Мастер-класс"],
  ["SINGLE_PRODUCT", "Отдельный продукт"],
];
const PRODUCT_TYPE_LABEL = Object.fromEntries(PRODUCT_TYPES);

export default function Admin() {
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "user";
  const isEditor = role === "curator";
  const visibleSections = useMemo(() => sectionsForRole(role), [role]);
  const allowedTabs = useMemo(() => new Set(visibleSections.map((s) => s.id)), [visibleSections]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || (role === "curator" ? "support" : "overview");
  const [tab, setTab] = useState(allowedTabs.has(initialTab) ? initialTab : (role === "curator" ? "support" : "overview"));
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  useEffect(() => {
    if (["structure", "sections", "materials", "relations", "instructions"].includes(tab)) setToolsExpanded(true);
  }, [tab]);
  const [pendingProductId, setPendingProductId] = useState("");
  const [pendingMaterialId, setPendingMaterialId] = useState(searchParams.get("edit") || "");
  const [pendingInstructionId, setPendingInstructionId] = useState("");
  const [pendingAction, setPendingAction] = useState(null); // { tab, kind: "create" }
  const [pendingFilter, setPendingFilter] = useState(null); // { tab, key, value }

  // Sync URL → state on external navigation. Also guard restricted tabs by URL.
  useEffect(() => {
    const t = searchParams.get("tab");
    const e = searchParams.get("edit");
    if (t) {
      if (allowedTabs.has(t)) {
        if (t !== tab) setTab(t);
      } else {
        // Restricted tab (e.g. editor tries /admin?tab=users) — bounce to overview.
        const next = new URLSearchParams(searchParams);
        next.delete("tab");
        setSearchParams(next, { replace: true });
        setTab(role === "curator" ? "support" : "overview");
      }
    }
    if (e) {
      setPendingMaterialId(e);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allowedTabs]);

  const changeTab = (v) => {
    if (!allowedTabs.has(v)) return; // silently refuse
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const goto = (targetTab, opts = {}) => {
    if (!allowedTabs.has(targetTab)) return;
    if (opts.productId) setPendingProductId(opts.productId);
    if (opts.materialId) setPendingMaterialId(opts.materialId);
    if (opts.instructionId) setPendingInstructionId(opts.instructionId);
    if (opts.action) setPendingAction({ tab: targetTab, kind: opts.action });
    if (opts.filter) setPendingFilter({ tab: targetTab, ...opts.filter });
    changeTab(targetTab);
  };

  return (
    <AdminNavCtx.Provider value={{
      goto,
      pendingProductId, clearPendingProductId: () => setPendingProductId(""),
      pendingMaterialId, clearPendingMaterialId: () => setPendingMaterialId(""),
      pendingInstructionId, clearPendingInstructionId: () => setPendingInstructionId(""),
      pendingAction, clearPendingAction: () => setPendingAction(null),
      pendingFilter, clearPendingFilter: () => setPendingFilter(null),
      role,
    }}>
    <div className="min-h-screen bg-cream flex flex-col" data-testid="admin-page" data-role={role}>
      <header className="flex items-center gap-3 px-5 pt-5 pb-3 max-w-7xl mx-auto w-full">
        <button
          onClick={() => nav("/hall")}
          data-testid="back-to-hall-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="В главный зал"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Anokina Studio · {roleLabel(role)}
          </div>
          <h1 className="font-serif text-2xl text-espresso -mt-0.5 truncate" data-testid="admin-title">
            {isEditor ? "Панель куратора" : "Панель управления"}
          </h1>
        </div>
        {(role === "owner" || role === "administrator") && (
          <a href="/legal" data-testid="admin-legal-center-link"
            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-[#D7C5AD] px-3 text-xs text-espresso hover:bg-white">
            Правовой центр <ExternalLink size={13} aria-hidden="true" />
          </a>
        )}
        <NotificationBell testid="admin-notification-bell" />
      </header>

      <div className="px-5 pb-8 flex-1 max-w-7xl mx-auto w-full">
        <Tabs value={tab} onValueChange={changeTab}>
          {tab !== "overview" && allowedTabs.has("overview") && (
            <button type="button" onClick={() => changeTab("overview")} className="lg:hidden mb-3 rounded-xl border hairline bg-white/80 px-4 py-2 text-sm text-espresso" data-testid="admin-mobile-back-workspace">← Моя мастерская</button>
          )}
          {tab !== "overview" && (
            <div className="lg:hidden mb-3">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Раздел</label>
              <Select value={tab} onValueChange={changeTab}>
                <SelectTrigger className="mt-1 h-11 rounded-xl bg-white" data-testid="admin-mobile-section-select"><SelectValue /></SelectTrigger>
                <SelectContent>{visibleSections.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`admin-mobile-section-option-${s.id}`}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {tab !== "overview" && allowedTabs.has("overview") && (
            <div className="hidden lg:flex items-center gap-2 mb-4">
              <button type="button" onClick={() => changeTab("overview")} className="rounded-xl border hairline px-4 py-2 text-sm text-espresso hover:bg-white" data-testid="admin-back-workspace">← Моя мастерская</button>
              <span className="text-sm text-muted-foreground">{visibleSections.find(s => s.id === tab)?.label}</span>
            </div>
          )}
          <div className={tab === "overview" ? "" : "lg:grid lg:grid-cols-[auto_minmax(0,1fr)] lg:gap-5"}>
            {tab !== "overview" && (
              <aside className={`hidden lg:flex flex-col self-start sticky top-3 rounded-2xl border hairline bg-white/80 p-2 gap-1 ${navCollapsed ? "w-14" : "w-52"}`} aria-label="Навигация панели управления">
                <button type="button" onClick={() => setNavCollapsed(v => !v)} aria-label={navCollapsed ? "Развернуть меню" : "Свернуть меню"} className="flex items-center justify-center min-h-10 rounded-xl hover:bg-sand/40">{navCollapsed ? <PanelLeftOpen size={18}/> : <PanelLeftClose size={18}/>}</button>
                {[
                  ["products", Package], ["editorial", BookOpen], ["media", Images],
                  ["users", Users], ["support", MessagesSquare], ["analytics", BarChart3],
                  ["access", KeyRound], ["comments", MessageCircleMore], ["mo_testing", ClipboardList],
                ].filter(([id]) => allowedTabs.has(id)).map(([id, Icon]) => (
                  <button type="button" key={id} title={visibleSections.find(s => s.id === id)?.label} onClick={() => changeTab(id)} data-testid={`admin-tab-${id}`}
                    className={`flex items-center gap-3 min-h-10 rounded-xl px-3 text-sm text-left hover:bg-sand/40 ${tab === id ? "bg-sand/60 font-medium text-espresso" : "text-espresso/80"}`}>
                    <Icon size={17} className="shrink-0"/>{!navCollapsed && <span className="truncate">{visibleSections.find(s => s.id === id)?.label}</span>}
                  </button>
                ))}
                <button type="button" onClick={() => setToolsExpanded(v => !v)} aria-expanded={toolsExpanded} className="flex items-center gap-3 min-h-10 rounded-xl px-3 text-sm text-espresso/80 hover:bg-sand/40"><Settings2 size={17}/>{!navCollapsed && <span>Инструменты</span>}</button>
                {toolsExpanded && [
                  ["structure", Layers3], ["sections", Layers3], ["materials", FileText],
                  ["relations", Link2], ["instructions", BookOpen],
                ].filter(([id]) => allowedTabs.has(id)).map(([id, Icon]) => (
                  <button type="button" key={id} onClick={() => changeTab(id)} title={visibleSections.find(s => s.id === id)?.label}
                    className={`flex items-center gap-3 min-h-10 rounded-xl px-3 text-xs text-left hover:bg-sand/40 ${tab === id ? "bg-sand/60" : ""}`}><Icon size={16}/>{!navCollapsed && visibleSections.find(s => s.id === id)?.label}</button>
                ))}
              </aside>
            )}
            <div className="min-w-0">
          <TabsContent value="overview"  className="mt-5"><Overview /></TabsContent>
          <TabsContent value="products"  className="mt-5"><ProductsPanel /></TabsContent>
          <TabsContent value="editorial" className="mt-5"><EditorialTabRedirect /></TabsContent>
          <TabsContent value="structure" className="mt-5"><StructurePanel /></TabsContent>
          <TabsContent value="sections"  className="mt-5"><SectionsPanel /></TabsContent>
          <TabsContent value="materials" className="mt-5"><MaterialsPanel /></TabsContent>
          <TabsContent value="media"     className="mt-5"><MediaLibraryPanel /></TabsContent>
          <TabsContent value="relations" className="mt-5"><RelationsPanel /></TabsContent>
          <TabsContent value="support"   className="mt-5"><SupportPanel /></TabsContent>
          <TabsContent value="comments"  className="mt-5"><StaffCommentsPanel /></TabsContent>
          <TabsContent value="analytics" className="mt-5"><AnalyticsPanel /></TabsContent>
          {allowedTabs.has("users")  && <TabsContent value="users"  className="mt-5"><UsersPanel /></TabsContent>}
          {allowedTabs.has("access") && <TabsContent value="access" className="mt-5"><AccessPanel /></TabsContent>}
          {allowedTabs.has("mo_testing") && <TabsContent value="mo_testing" className="mt-5"><MOTestingAdmin /></TabsContent>}
          <TabsContent value="instructions" className="mt-5"><InstructionsPanel /></TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
    </AdminNavCtx.Provider>
  );
}

// ---------- Overview ----------
function Overview() {
  const { goto, role } = useAdminNav();
  const cards = [
    { id: "editorial", label: "Редакция", detail: "Книги, статьи и публикации", Icon: BookOpen },
    { id: "media", label: "Медиатека", detail: "Фотографии и файлы", Icon: Images },
    { id: "products", label: "Продукты", detail: "Программы и пространства", Icon: Package },
    { id: "users", label: "Пользователи", detail: "Аккаунты и доступы", Icon: Users },
    { id: "support", label: "Обратная связь", detail: "Обращения и комментарии", Icon: MessagesSquare },
    { id: "analytics", label: "Аналитика", detail: "Активность и результаты", Icon: BarChart3 },
  ];
  const available = sectionsForRole(role);
  const allowed = new Set(available.map(s => s.id));
  return (
    <section data-testid="admin-overview" className="max-w-5xl mx-auto py-3 sm:py-8">
      <div className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Рабочее пространство</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-espresso mt-2">Моя мастерская</h2>
        <p className="text-sm text-muted-foreground mt-2">Выберите, с чем хотите работать.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-workspace-grid">
        {cards.filter(c => allowed.has(c.id)).map(({id,label,detail,Icon}) => (
          <button type="button" key={id} onClick={() => goto(id)} data-testid={`admin-workspace-${id}`}
            className="min-h-36 rounded-2xl bg-white/85 border hairline p-5 text-left flex flex-col items-start gap-3 hover:bg-sand/30 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso transition">
            <span className="rounded-full bg-sand/60 p-3"><Icon size={21} className="text-espresso"/></span>
            <span className="font-serif text-xl text-espresso">{label}</span>
            <span className="text-xs text-muted-foreground">{detail}</span>
          </button>
        ))}
      </div>
      <div className="mt-7 flex flex-wrap gap-2 text-sm">
        {[
          ["access", "Доступы"], ["comments", "Комментарии"], ["mo_testing", "Отзывы о Мастере заказа"],
          ["instructions", "Инструкции"], ["structure", "Структура"], ["sections", "Разделы"],
          ["materials", "Материалы"], ["relations", "Связи"],
        ].filter(([id]) => allowed.has(id)).map(([id,label]) => (
          <button key={id} type="button" onClick={() => goto(id)} className="rounded-xl border hairline bg-white/60 px-3 py-2 text-xs text-espresso hover:bg-sand/30">{label}</button>
        ))}
      </div>
    </section>
  );
}

function QuickActions() {
  const { goto } = useAdminNav();
  const actions = [
    { key: "new-material",   label: "Новый материал",    Icon: Plus,     onClick: () => goto("materials",    { action: "create" }) },
    { key: "upload-media",   label: "Загрузить медиа",   Icon: Upload,   onClick: () => goto("media",        { action: "create" }) },
    { key: "new-instruction",label: "Создать инструкцию",Icon: BookOpen, onClick: () => goto("instructions", { action: "create" }) },
  ];
  return (
    <div className="rounded-2xl bg-white border hairline p-3" data-testid="overview-quick-actions">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-3 h-3 text-espresso" />
        <div className="text-[10px] uppercase tracking-widest text-espresso">Быстрые действия</div>
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {actions.map(({ key, label, Icon, onClick }) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            data-testid={`quick-${key}`}
            className="w-full flex items-center gap-2 rounded-lg border hairline bg-sand/15 hover:bg-sand/35 px-2.5 py-1.5 text-sm text-espresso text-left min-h-[36px]"
          >
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AttentionPanel({ items, loading }) {
  return (
    <div className="rounded-2xl bg-white border hairline p-3" data-testid="overview-attention">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-3 h-3 text-amber-700" />
        <div className="text-[10px] uppercase tracking-widest text-espresso">Требует внимания</div>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground">Загрузка…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic" data-testid="attention-empty">
          Всё в порядке.
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((a) => (
            <li key={a.key}>
              <button
                type="button"
                onClick={a.onClick}
                data-testid={`attention-item-${a.key}`}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 px-2.5 py-1.5 text-left min-h-[36px]"
              >
                <span className="text-sm text-amber-900 truncate">{a.label}</span>
                <span className="font-serif text-amber-900 text-base leading-none">{a.value}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Russian action verbs — gendered by object type.
// материал (masc): изменён / опубликован.
// инструкция (fem): изменена / опубликована.
const ACTIVITY_VERB = {
  material:    { edited: "Изменён",  published: "Опубликован" },
  instruction: { edited: "Изменена", published: "Опубликована" },
};
const ACTIVITY_LABEL = { material: "Материал", instruction: "Инструкция" };

function RecentActivityPanel({ items, loading }) {
  const { goto } = useAdminNav();
  const openItem = (it) => {
    if (it.type === "material") {
      goto("materials", { materialId: it.id });
    } else if (it.type === "instruction") {
      goto("instructions", { instructionId: it.id });
    }
  };
  return (
    <div className="rounded-2xl bg-white border hairline p-3" data-testid="overview-recent-activity">
      <div className="text-[10px] uppercase tracking-widest text-espresso mb-2">Последняя активность</div>
      {loading ? (
        <div className="text-xs text-muted-foreground">Загрузка…</div>
      ) : !items || items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic">Пока пусто</p>
      ) : (
        <ul className="space-y-1" data-testid="recent-activity-list">
          {items.map((it) => {
            const verb = (ACTIVITY_VERB[it.type] || {})[it.action] || "Изменено";
            return (
              <li key={`${it.type}-${it.id}`}>
                <button
                  type="button"
                  onClick={() => openItem(it)}
                  data-testid={`recent-activity-${it.type}-${it.id}`}
                  className="w-full rounded-lg hover:bg-sand/20 px-2 py-1.5 -mx-2 text-left min-h-[52px]"
                >
                  <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                    {ACTIVITY_LABEL[it.type] || it.type}
                  </div>
                  <div className="text-sm text-espresso font-medium truncate">«{it.title}»</div>
                  <div className="text-[11px] text-muted-foreground">
                    {verb} · {formatDateShort(it.updated_at)}{formatTimeShort(it.updated_at) ? ` · ${formatTimeShort(it.updated_at)}` : ""}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatTimeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // Same-day activity → show HH:MM; older → omit time.
    const now = new Date();
    if (d.toDateString() !== now.toDateString()) return "";
    return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function RecentPanel({ title, items, testid, onOpen }) {
  return (
    <div className="rounded-2xl bg-white border hairline p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{title}</div>
      {(items ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic">Пока пусто</p>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="border-t hairline first:border-t-0 flex items-center gap-1">
              <button
                type="button"
                onClick={() => onOpen?.(m)}
                className="flex-1 text-left flex items-center gap-3 py-2 hover:bg-sand/20 rounded-lg px-1 -mx-1 cursor-pointer"
                data-testid={`${testid}-item-${m.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-espresso truncate">{m.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {MATERIAL_TYPE_LABEL[m.material_type] || m.material_type} · {STATUS_LABEL[m.status] || m.status}
                    {m.is_free && " · Бесплатно"}
                  </div>
                </div>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {formatDateShort(m.updated_at)}
                </span>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); window.open(`/admin/preview/materials/${m.id}`, "_blank", "noopener"); }}
                data-testid={`${testid}-preview-${m.id}`}
                title="Предпросмотр"
                className="p-2 rounded-full hover:bg-sand/50 min-w-[40px] min-h-[40px] flex items-center justify-center"
              >
                <Eye className="w-4 h-4 text-espresso" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDateShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short" }); }
  catch { return ""; }
}

// ---------- Products ----------
function useCrud(endpoint) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(endpoint); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async (data) => {
    try {
      if (data.id) {
        const { id, created_at, updated_at, ...body } = data;
        await api.patch(`${endpoint}/${id}`, body);
        toast.success("Сохранено");
      } else {
        await api.post(endpoint, data);
        toast.success("Создано");
      }
      setEditing(null);
      load();
    } catch (e) {
      // If a stale row was edited (record was deleted / id changed since list
      // was loaded), refresh the list so the user can retry cleanly.
      if (e.response?.status === 404) {
        toast.error("Запись не найдена — обновляем список");
        setEditing(null);
        load();
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Удалено");
      load();
    } catch (e) {
      if (e.response?.status === 404) { toast.error("Запись уже удалена"); load(); }
      else toast.error(formatApiError(e));
    }
  };
  return { items, editing, setEditing, save, remove, reload: load };
}

function ProductsPanel() {
  const { goto } = useAdminNav();
  const nav = useNavigate();
  const { items, editing, setEditing, save, remove, reload } = useCrud("/admin/products");
  const reorder = async (id, delta) => {
    const idx = items.findIndex((x) => x.id === id);
    const nb = items[idx + delta];
    if (!nb) return;
    try {
      await api.post("/admin/products/reorder", {
        items: [
          { id: items[idx].id, sort_order: nb.sort_order ?? 0 },
          { id: nb.id, sort_order: items[idx].sort_order ?? 0 },
        ],
      });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="admin-products">
      <PanelHeader title="Продукты"
        onNew={() => setEditing({ type: "VOLUME", status: "draft", is_public: false, sort_order: (items.length + 1) * 10 })}
        testid="admin-new-product" />
      <div className="mb-3 rounded-2xl bg-white border hairline p-3 flex items-center gap-3" data-testid="admin-open-library-workspace">
        <button type="button" onClick={() => nav("/admin/open-library")} className="flex-1 min-w-0 text-left">
          <div className="font-serif text-lg text-espresso truncate">Открытая библиотека</div>
          <div className="text-xs text-muted-foreground">
            Публичное пространство знаний · управляется через Редакцию
          </div>
        </button>
        <Button size="sm" onClick={() => nav("/admin/open-library")}>
          Управлять
        </Button>
      </div>

      <ul className="space-y-2" data-testid="admin-products-list">
        {items.map((p, idx) => (
          <li key={p.id} className="rounded-2xl bg-white border hairline p-3 flex items-center gap-3"
              data-testid={`admin-product-${p.id}`}>
            <button type="button" onClick={() => nav(`/admin/products/${p.id}`)}
              className="flex-1 min-w-0 text-left"
              data-testid={`admin-product-open-${p.id}`}>
              <div className="font-serif text-lg text-espresso truncate">{p.title}</div>
              <div className="text-xs text-muted-foreground">
                {PRODUCT_TYPE_LABEL[p.type] || p.type} · {STATUS_LABEL[p.status] || p.status}
                {p.is_public ? " · публичный" : ""} · #{p.sort_order ?? 0}
              </div>
            </button>
            <Button size="sm" onClick={() => nav(`/admin/products/${p.id}`)}
              data-testid={`admin-product-manage-${p.id}`}>
              Управлять
            </Button>
            <button onClick={() => reorder(p.id, -1)} className="p-1.5 opacity-60 hover:opacity-100" aria-label="Выше">
              <ChevronUp className="w-4 h-4" />
            </button>
            <button onClick={() => reorder(p.id, +1)} className="p-1.5 opacity-60 hover:opacity-100" aria-label="Ниже">
              <ChevronDown className="w-4 h-4" />
            </button>
            <button onClick={() => setEditing(p)} className="p-1.5 opacity-60 hover:opacity-100" aria-label="Редактировать"
              data-testid={`admin-product-edit-${p.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(p.id)} className="p-1.5 opacity-60 hover:opacity-100 text-red-600" aria-label="Удалить">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      <ProductEditDialog open={!!editing} onClose={() => setEditing(null)} data={editing} onSave={save} />
    </div>
  );
}

// ---------- Structure (visual tree Product → Sections → Materials) ----------
function StructurePanel() {
  const { pendingProductId, clearPendingProductId, goto } = useAdminNav();
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [sections, setSections] = useState([]);
  const [sectionMaterials, setSectionMaterials] = useState({}); // {sid: [material,...]}
  const [expanded, setExpanded] = useState({});
  const [attachOpen, setAttachOpen] = useState(null); // sid to attach to

  const loadProducts = async () => {
    const { data } = await api.get("/admin/products");
    setProducts(data);
    // Apply pending selection if requested via navigation, else default to first product.
    if (pendingProductId) {
      setProductId(pendingProductId);
      clearPendingProductId();
    } else if (data[0] && !productId) {
      setProductId(data[0].id);
    }
  };
  const loadSections = async (pid) => {
    if (!pid) { setSections([]); return; }
    const { data } = await api.get(`/admin/sections-with-counts?product_id=${pid}`);
    setSections(data);
  };
  const loadMaterials = async (sid) => {
    const { data } = await api.get(`/admin/sections/${sid}/materials`);
    setSectionMaterials((x) => ({ ...x, [sid]: data }));
  };

  useEffect(() => { loadProducts().catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { loadSections(productId).catch((e) => toast.error(formatApiError(e))); }, [productId]);

  const toggle = async (sid) => {
    const next = !expanded[sid];
    setExpanded((x) => ({ ...x, [sid]: next }));
    if (next && !sectionMaterials[sid]) await loadMaterials(sid);
  };

  const detach = async (sid, mid) => {
    try {
      await api.delete(`/admin/sections/${sid}/materials/${mid}`);
      await loadMaterials(sid);
      loadSections(productId);
      toast.success("Отвязано");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reorderSection = async (id, delta) => {
    const idx = sections.findIndex((x) => x.id === id);
    const nb = sections[idx + delta];
    if (!nb) return;
    try {
      await api.post("/admin/sections/reorder", {
        items: [
          { id: sections[idx].id, sort_order: nb.sort_order ?? 0 },
          { id: nb.id, sort_order: sections[idx].sort_order ?? 0 },
        ],
      });
      loadSections(productId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-structure">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <label className="text-xs uppercase tracking-widest text-muted-foreground">Продукт</label>
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger className="h-10 rounded-xl sm:w-80" data-testid="structure-product-select">
            <SelectValue placeholder="Выберите продукт" />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {sections.length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic text-center py-8">
          В этом продукте пока нет разделов. Создайте их во вкладке «Разделы».
        </p>
      ) : (
        <ul className="space-y-2" data-testid="structure-tree">
          {sections.map((s, idx) => (
            <li key={s.id} className="rounded-2xl bg-white border hairline" data-testid={`structure-section-${s.id}`}>
              <div className="p-3 flex items-center gap-2">
                <button
                  onClick={() => toggle(s.id)}
                  className="p-1 rounded-full hover:bg-sand/50"
                  aria-label={expanded[s.id] ? "Свернуть" : "Раскрыть"}
                  data-testid={`structure-toggle-${s.id}`}
                >
                  {expanded[s.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4 rotate-90" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-espresso truncate">{s.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {STATUS_LABEL[s.status] || s.status} · материалов: {s.materials_count}
                  </div>
                </div>
                <button onClick={() => reorderSection(s.id, -1)} disabled={idx === 0} className="p-2 rounded-full hover:bg-sand/50 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => reorderSection(s.id, +1)} disabled={idx === sections.length - 1} className="p-2 rounded-full hover:bg-sand/50 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button
                  onClick={() => setAttachOpen(s.id)}
                  className="btn-pill bg-espresso text-white h-8 px-3 text-xs"
                  data-testid={`structure-attach-${s.id}`}
                >
                  <Plus className="w-3 h-3 mr-1 inline" /> Материал
                </button>
              </div>
              {expanded[s.id] && (
                <ul className="border-t hairline">
                  {(sectionMaterials[s.id] || []).length === 0 ? (
                    <li className="px-4 py-3 text-sm text-muted-foreground italic">Материалов пока нет</li>
                  ) : (
                    (sectionMaterials[s.id] || []).map((m) => (
                      <li key={m.id} className="px-2 border-t hairline first:border-t-0"
                          data-testid={`structure-section-${s.id}-material-${m.id}`}>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => goto("materials", { materialId: m.id })}
                            data-testid={`structure-material-open-${m.id}`}
                            className="flex-1 min-w-0 text-left py-2 px-2 -mx-1 rounded-lg hover:bg-sand/30 cursor-pointer"
                          >
                            <div className="text-espresso truncate">{m.title}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {MATERIAL_TYPE_LABEL[m.material_type] || m.material_type} · {STATUS_LABEL[m.status] || m.status}{m.is_free && " · Бесплатно"}
                            </div>
                          </button>
                          <button onClick={() => detach(s.id, m.id)} className="p-2 rounded-full hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center" title="Отвязать">
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      <AttachMaterialDialog
        open={!!attachOpen}
        sectionId={attachOpen}
        onClose={() => setAttachOpen(null)}
        onAttached={async () => {
          if (attachOpen) await loadMaterials(attachOpen);
          loadSections(productId);
        }}
      />
    </div>
  );
}

function AttachMaterialDialog({ open, sectionId, onClose, onAttached }) {
  const [q, setQ] = useState("");
  const [materials, setMaterials] = useState([]);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/admin/materials"); setMaterials(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return materials;
    return materials.filter((m) => m.title.toLowerCase().includes(s));
  }, [q, materials]);
  const attach = async (mid) => {
    try {
      await api.post(`/admin/sections/${sectionId}/materials`, { material_id: mid, sort_order: 0 });
      toast.success("Привязано");
      onAttached();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">Привязать материал</DialogTitle></DialogHeader>
        <Input placeholder="Поиск по названию" value={q} onChange={(e) => setQ(e.target.value)} data-testid="attach-material-search" className="rounded-xl" />
        <ul className="mt-2 space-y-1 max-h-72 overflow-y-auto">
          {filtered.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => attach(m.id)}
                data-testid={`attach-option-${m.id}`}
                className="w-full text-left rounded-xl px-3 py-2 hover:bg-sand/40"
              >
                <div className="font-medium text-espresso truncate">{m.title}</div>
                <div className="text-xs text-muted-foreground">{MATERIAL_TYPE_LABEL[m.material_type] || m.material_type} · {STATUS_LABEL[m.status] || m.status}</div>
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="text-sm text-muted-foreground italic px-3 py-2">Ничего не найдено</li>}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Sections ----------
function SectionsPanel() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadProducts = async () => {
    try { const { data } = await api.get("/admin/products"); setProducts(data); if (data[0] && !productId) setProductId(data[0].id); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadSections = async (pid) => {
    if (!pid) { setItems([]); return; }
    try { const { data } = await api.get(`/admin/sections-with-counts?product_id=${pid}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { loadSections(productId); }, [productId]);

  const save = async (data) => {
    try {
      if (data.id) {
        const { id, created_at, updated_at, materials_count, ...body } = data;
        await api.patch(`/admin/sections/${id}`, body);
        toast.success("Сохранено");
      } else {
        await api.post("/admin/sections", data);
        toast.success("Создано");
      }
      setEditing(null);
      loadSections(productId);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/sections/${id}`); toast.success("Удалено"); loadSections(productId); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reorder = async (id, delta) => {
    const idx = items.findIndex((x) => x.id === id);
    const nb = items[idx + delta];
    if (!nb) return;
    try {
      await api.post("/admin/sections/reorder", {
        items: [
          { id: items[idx].id, sort_order: nb.sort_order ?? 0 },
          { id: nb.id, sort_order: items[idx].sort_order ?? 0 },
        ],
      });
      loadSections(productId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-sections">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-3">
        <label className="text-xs uppercase tracking-widest text-muted-foreground">Продукт</label>
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger className="h-10 rounded-xl sm:w-72" data-testid="sections-product-select">
            <SelectValue placeholder="Выберите продукт" />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="sm:ml-auto">
          <Button
            onClick={() => setEditing({ product_id: productId, status: "draft", sort_order: (items.length + 1) * 10 })}
            className="btn-pill bg-espresso hover:bg-espressoDark text-white h-auto py-2 px-4 text-sm"
            data-testid="admin-new-section" disabled={!productId}
          >
            <Plus className="w-4 h-4 mr-1" /> Создать
          </Button>
        </div>
      </div>
      <ItemList items={items} onEdit={setEditing} onDelete={remove}
        onUp={(id) => reorder(id, -1)} onDown={(id) => reorder(id, +1)}
        testidPrefix="admin-section"
        renderTitle={(it) => it.title}
        renderSub={(it) => `${STATUS_LABEL[it.status] || it.status} · #${it.sort_order ?? 0} · материалов: ${it.materials_count ?? 0}`}
      />
      <SectionEditDialog open={!!editing} onClose={() => setEditing(null)} data={editing} onSave={save} products={products} />
    </div>
  );
}

// ---------- Materials ----------
function MaterialsPanel() {
  const {
    pendingMaterialId, clearPendingMaterialId,
    pendingAction, clearPendingAction,
    pendingFilter, clearPendingFilter,
  } = useAdminNav();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [noCoverOnly, setNoCoverOnly] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/admin/materials"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Auto-open editor when Materials tab is entered with a preselected id.
  useEffect(() => {
    if (!pendingMaterialId || items.length === 0) return;
    const m = items.find((x) => x.id === pendingMaterialId);
    if (m) {
      setEditing(m);
      clearPendingMaterialId();
    }
  }, [pendingMaterialId, items, clearPendingMaterialId]);

  // Overview → Quick Action "Новый материал" opens the create dialog.
  useEffect(() => {
    if (pendingAction?.tab === "materials" && pendingAction?.kind === "create") {
      setEditing({});
      clearPendingAction();
    }
  }, [pendingAction, clearPendingAction]);

  // Overview → Attention "Материалы без обложки" enables the no-cover filter.
  useEffect(() => {
    if (pendingFilter?.tab === "materials" && pendingFilter?.key === "no_cover") {
      setNoCoverOnly(true);
      clearPendingFilter();
    }
  }, [pendingFilter, clearPendingFilter]);

  const visibleItems = noCoverOnly
    ? items.filter((m) => !m.cover_image || !String(m.cover_image).trim())
    : items;

  const save = async (data) => {
    try {
      if (data.__skipPatch) {
        // Backend already applied a dedicated action (e.g., publish endpoint). Just refresh list.
        setEditing(null);
        load();
        return;
      }
      if (data.id) {
        const { id, created_at, updated_at, ...body } = data;
        await api.patch(`/admin/materials/${id}`, body);
        toast.success("Материал сохранён");
      } else {
        const { data: created } = await api.post("/admin/materials", data);
        toast.success("Материал создан");
        setEditing(created);
        load();
        return;
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/materials/${id}`); toast.success("Удалено"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const viewPublic = (m, e) => {
    e?.stopPropagation?.();
    const slug = m.slug || m.id;
    window.open(`/library/${encodeURIComponent(slug)}`, "_blank", "noopener");
  };

  const preview = (m, e) => {
    e?.stopPropagation?.();
    window.open(`/admin/preview/materials/${m.id}`, "_blank", "noopener");
  };

  return (
    <div data-testid="admin-materials">
      <PanelHeader title="Материалы"
        onNew={() => setEditing({ material_type: "ARTICLE", status: "draft", is_free: false, sort_order: (items.length + 1) * 10 })}
        testid="admin-new-material" />
      <div className="flex items-center gap-2 mb-2 text-xs">
        <button
          type="button"
          onClick={() => setNoCoverOnly((v) => !v)}
          data-testid="materials-filter-no-cover"
          className={`chip border hairline px-2.5 h-7 min-h-[28px] ${noCoverOnly ? "bg-amber-100 text-amber-900 border-amber-200" : "bg-white text-espresso"}`}
        >
          {noCoverOnly ? "✓ " : ""}Только без обложки
        </button>
        {noCoverOnly && (
          <span className="text-[11px] text-muted-foreground" data-testid="materials-filter-hint">
            Показаны материалы, которым нужна обложка перед публикацией
          </span>
        )}
      </div>
      <ItemList items={visibleItems} onEdit={setEditing} onDelete={remove}
        onRowClick={(m) => setEditing(m)}
        renderAction={(m) => (
          <div className="flex items-center gap-1.5">
            {(!m.cover_image || !String(m.cover_image).trim()) && (
              <span
                data-testid={`admin-material-no-cover-${m.id}`}
                className="chip bg-amber-100 text-amber-900 border border-amber-200 h-7 px-2 text-[10px] uppercase tracking-widest whitespace-nowrap"
                title="Без обложки"
              >
                Без обложки
              </span>
            )}
            <button
              onClick={(e) => preview(m, e)}
              data-testid={`admin-material-preview-${m.id}`}
              className="chip bg-white text-espresso border border-[#E1D2B8] hover:bg-sand/40 h-9 px-3 text-xs flex items-center gap-1 min-h-[44px] whitespace-nowrap"
              title="Предпросмотр (админ)"
            >
              <Eye className="w-3.5 h-3.5" /> Предпросмотр
            </button>
            {m.status === "published" && (
              <button
                onClick={(e) => viewPublic(m, e)}
                data-testid={`admin-material-view-${m.id}`}
                className="chip bg-white text-espresso border border-[#E1D2B8] hover:bg-sand/40 h-9 px-3 text-xs flex items-center gap-1 min-h-[44px] whitespace-nowrap"
                title="Открыть публичную страницу"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Посмотреть
              </button>
            )}
          </div>
        )}
        testidPrefix="admin-material"
        renderTitle={(it) => it.title}
        renderSub={(it) => materialSubtitle(it)}
      />
      <MaterialEditDialog open={!!editing} onClose={() => setEditing(null)} data={editing} onSave={save} />
    </div>
  );
}

function MaterialEditDialog({ open, onClose, data, onSave }) {
  const [form, setForm] = useState({});
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [linkedSections, setLinkedSections] = useState([]);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaOnPick, setMediaOnPick] = useState(null); // callback to receive selected url

  useEffect(() => { setForm(data || {}); }, [data]);

  const loadRelations = async () => {
    if (!data?.id) { setLinkedProducts([]); setLinkedSections([]); return; }
    try {
      const { data: r } = await api.get(`/admin/materials/${data.id}/relations`);
      setLinkedProducts(r.products || []);
      setLinkedSections(r.sections || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [p, s] = await Promise.all([api.get("/admin/products"), api.get("/admin/sections")]);
        setProducts(p.data); setSections(s.data);
      } catch { /* noop */ }
      await loadRelations();
    })();
    // eslint-disable-next-line
  }, [open, data?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const attachProduct = async (pid) => {
    try {
      await api.post(`/admin/products/${pid}/materials`, { material_id: form.id, sort_order: 0 });
      toast.success("Привязано к продукту");
      loadRelations();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const detachProduct = async (pid) => {
    try { await api.delete(`/admin/products/${pid}/materials/${form.id}`); loadRelations(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const attachSection = async (sid) => {
    try {
      await api.post(`/admin/sections/${sid}/materials`, { material_id: form.id, sort_order: 0 });
      toast.success("Привязано к разделу");
      loadRelations();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const detachSection = async (sid) => {
    try { await api.delete(`/admin/sections/${sid}/materials/${form.id}`); loadRelations(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!open) return null;
  const isNew = !form.id;

  const openMediaPicker = (onPick) => { setMediaOnPick(() => onPick); setMediaOpen(true); };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-espresso">
              {isNew ? "Новый материал" : "Редактирование материала"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Публикация — prominent block near top */}
            <PublicationBlock
              status={form.status || "draft"}
              onStatusChange={(v) => set("status", v)}
              isFree={!!form.is_free}
              onFreeChange={(v) => set("is_free", v)}
              publishAt={form.publish_at || null}
              materialId={form.id}
              onSchedule={async (utcIso) => {
                if (!form.id) { toast.error("Сначала сохраните материал как черновик"); return; }
                try {
                  const { data } = await api.post(`/admin/materials/${form.id}/schedule`, { publish_at: utcIso });
                  setForm(data);
                  toast.success("Публикация запланирована");
                } catch (e) { toast.error(formatApiError(e)); throw e; }
              }}
              onCancelSchedule={async () => {
                try {
                  const { data } = await api.delete(`/admin/materials/${form.id}/schedule`);
                  setForm(data);
                  toast.success("Планирование отменено");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              onPublishNow={async () => {
                try {
                  const { data } = await api.post(`/admin/materials/${form.id}/publish`);
                  setForm(data);
                  toast.success("Материал опубликован");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Название" v={form.title} onChange={(v) => set("title", v)} testid="material-title" />
              <Field label="Slug" v={form.slug} onChange={(v) => set("slug", v)} testid="material-slug" />
              <SelectField label="Тип" v={form.material_type} onChange={(v) => set("material_type", v)}
                testid="material-type" options={MATERIAL_TYPES} />
            </div>

            <Field label="Краткое описание" v={form.short_description} onChange={(v) => set("short_description", v)} testid="material-short" multiline />

            {/* Cover image with picker */}
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Обложка</label>
              <div className="mt-1 flex items-center gap-3">
                {form.cover_image ? (
                  <img src={form.cover_image} alt="cover" className="w-24 h-24 rounded-2xl object-cover border hairline" onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-sand/40 border hairline flex items-center justify-center text-espresso/40">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input value={form.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)}
                         placeholder="URL или выберите из Медиатеки" className="rounded-xl h-10" data-testid="material-cover" />
                  <Button type="button" variant="outline" className="btn-pill h-9"
                    onClick={() => openMediaPicker((url) => set("cover_image", url))}
                    data-testid="material-cover-from-media">
                    Из Медиатеки
                  </Button>
                </div>
              </div>
            </div>

            {/* Rich body */}
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Содержимое</label>
              <div className="mt-1" data-testid="material-body-editor">
                <RichEditor
                  value={form.body || ""}
                  onChange={(html) => set("body", html)}
                  placeholder="Начните писать статью…"
                  onInsertImageClick={(insert) => openMediaPicker((url) => insert(url))}
                />
              </div>
            </div>

            {/* Relations (only for existing material) */}
            {!isNew && (
              <div className="rounded-2xl border hairline p-4 bg-white/50">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Связанные продукты</div>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="material-linked-products">
                  {linkedProducts.map((p) => (
                    <span key={p.id} className="chip bg-sand/60 text-espresso flex items-center gap-1">
                      {p.title}
                      <button onClick={() => detachProduct(p.id)} className="ml-1 hover:text-red-600" aria-label="Отвязать"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {linkedProducts.length === 0 && <span className="text-xs text-muted-foreground italic">Пока не привязан</span>}
                </div>
                <Select value="" onValueChange={attachProduct}>
                  <SelectTrigger className="h-9 rounded-xl" data-testid="material-attach-product"><SelectValue placeholder="+ Привязать к продукту" /></SelectTrigger>
                  <SelectContent>
                    {products.filter((p) => !linkedProducts.find((x) => x.id === p.id)).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="text-xs uppercase tracking-widest text-muted-foreground mt-4 mb-2">Связанные разделы</div>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="material-linked-sections">
                  {linkedSections.map((s) => {
                    const p = products.find((x) => x.id === s.product_id);
                    return (
                      <span key={s.id} className="chip bg-sand/60 text-espresso flex items-center gap-1">
                        {p ? `${p.title} → ${s.title}` : s.title}
                        <button onClick={() => detachSection(s.id)} className="ml-1 hover:text-red-600" aria-label="Отвязать"><X className="w-3 h-3" /></button>
                      </span>
                    );
                  })}
                  {linkedSections.length === 0 && <span className="text-xs text-muted-foreground italic">Пока не привязан</span>}
                </div>
                <Select value="" onValueChange={attachSection}>
                  <SelectTrigger className="h-9 rounded-xl" data-testid="material-attach-section"><SelectValue placeholder="+ Привязать к разделу" /></SelectTrigger>
                  <SelectContent>
                    {sections.filter((s) => !linkedSections.find((x) => x.id === s.id)).map((s) => {
                      const p = products.find((x) => x.id === s.product_id);
                      return <SelectItem key={s.id} value={s.id}>{p ? `${p.title} → ${s.title}` : s.title}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Дополнительные настройки — collapsible, hidden by default */}
            <AdvancedSettings
              sortOrder={form.sort_order}
              onSortOrderChange={(v) => set("sort_order", v)}
            />
          </div>

          <DialogFooter className="mt-4 gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={onClose} className="btn-pill" data-testid="material-cancel">Закрыть</Button>
            {!isNew && (
              <Button
                type="button"
                variant="outline"
                className="btn-pill"
                onClick={() => window.open(`/admin/preview/materials/${form.id}`, "_blank", "noopener")}
                data-testid="material-preview"
              >
                Предпросмотр
              </Button>
            )}
            {!isNew && form.status !== "published" && (
              <Button
                type="button"
                onClick={async () => {
                  if (!form.cover_image || !String(form.cover_image).trim()) {
                    toast.error("Добавьте обложку перед публикацией материала.");
                    return;
                  }
                  try {
                    const { data } = await api.post(`/admin/materials/${form.id}/publish`);
                    toast.success("Материал опубликован");
                    onSave({ ...data, __skipPatch: true });
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="btn-pill bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="material-publish"
              >
                Опубликовать
              </Button>
            )}
            <Button
              onClick={() => {
                if (form.status === "published"
                    && (!form.cover_image || !String(form.cover_image).trim())) {
                  toast.error("Добавьте обложку перед публикацией материала.");
                  return;
                }
                onSave(form);
              }}
              className="btn-pill bg-espresso hover:bg-espressoDark text-white"
              data-testid="material-save"
            >
              {isNew ? "Создать черновик" : (form.status === "published" ? "Сохранить изменения" : "Сохранить")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={mediaOpen}
        onClose={() => { setMediaOpen(false); setMediaOnPick(null); }}
        onPick={(url) => { mediaOnPick?.(url); setMediaOpen(false); setMediaOnPick(null); }}
      />
    </>
  );
}

// Protected paid media remain visible to Owner without exposing raw bytes.
function AdminMediaThumbnail({ url, alt, className }) {
  const resolved = useEditorialMediaUrl(url);
  return resolved ? <img src={resolved} alt={alt || ""} className={className} /> :
    <ImageIcon className="w-8 h-8 text-espresso/40" />;
}

// ---------- Media Library ----------
function MediaLibraryPanel() {
  const { pendingAction, clearPendingAction } = useAdminNav();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [mediaType, setMediaType] = useState("all");
  const [uploadZone, setUploadZone] = useState("public");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [tag, setTag] = useState("");
  const [productId, setProductId] = useState("all");
  const [sort, setSort] = useState("newest");
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { media, usage }
  const fileRef = useRef(null);

  // Overview → Quick Action "Загрузить медиа" opens the OS file picker.
  useEffect(() => {
    if (pendingAction?.tab === "media" && pendingAction?.kind === "create") {
      fileRef.current?.click();
      clearPendingAction();
    }
  }, [pendingAction, clearPendingAction]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (mediaType && mediaType !== "all") params.set("media_type", mediaType);
      if (tag.trim()) params.set("tag", tag.trim());
      if (productId && productId !== "all") params.set("product_id", productId);
      if (sort) params.set("sort", sort);
      const qs = params.toString();
      const url = qs ? `/admin/media/search?${qs}` : "/admin/media";
      const { data } = await api.get(url);
      setItems(zoneFilter === "all" ? data : (data || []).filter((m) => zoneFilter === "legacy" ? !m.access_zone : m.access_zone === zoneFilter));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/products"); setProducts(data); } catch {}
    })();
  }, []);
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, mediaType, tag, productId, sort, zoneFilter]); // eslint-disable-line

  const upload = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.replace(/\.[^.]+$/, ""));
      form.append("access_zone", uploadZone);
      await api.post("/admin/media/upload-file", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Загружено");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const requestDelete = async (media) => {
    try {
      const { data } = await api.get(`/admin/media/${media.id}/usage`);
      setConfirmDelete({ media, usage: data });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const performDelete = async (force = false) => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/media/${confirmDelete.media.id}${force ? "?force=true" : ""}`);
      toast.success("Удалено");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async (data) => {
    const { id, created_at, updated_at, file_url, file_type, uploaded_by, ...body } = data;
    try {
      await api.patch(`/admin/media/${id}`, body);
      toast.success("Сохранено");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetFilters = () => {
    setQ(""); setMediaType("all"); setTag(""); setProductId("all"); setSort("newest"); setZoneFilter("all");
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Поиск: название, тег, alt, описание, имя файла"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 rounded-xl h-10 bg-white" data-testid="media-search" />
          </div>
          <input
            ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="media-file-input"
          />
          <Select value={uploadZone} onValueChange={setUploadZone}>
            <SelectTrigger className="h-10 w-[160px] rounded-xl bg-white" data-testid="media-upload-zone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Открытые фото</SelectItem>
              <SelectItem value="paid">Закрытые фото</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-pill bg-espresso hover:bg-espressoDark text-white h-auto py-2 px-4 text-sm"
            data-testid="media-upload-button"
          >
            <Upload className="w-4 h-4 mr-1" /> {uploading ? "Загрузка…" : "Загрузить"}
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Select value={zoneFilter} onValueChange={setZoneFilter}>
            <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="media-filter-zone"><SelectValue placeholder="Зона" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все зоны</SelectItem>
              <SelectItem value="public">Открытые</SelectItem>
              <SelectItem value="paid">Закрытые</SelectItem>
              <SelectItem value="legacy">Без зоны (архив)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={mediaType} onValueChange={setMediaType}>
            <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="media-filter-type">
              <SelectValue placeholder="Тип" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все типы</SelectItem>
              {MEDIA_TYPES.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input placeholder="Тег"
                 value={tag} onChange={(e) => setTag(e.target.value)}
                 className="h-10 rounded-xl bg-white" data-testid="media-filter-tag" />
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="media-filter-product">
              <SelectValue placeholder="Продукт" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все продукты</SelectItem>
              {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="media-filter-sort">
              <SelectValue placeholder="Сортировка" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Сначала новые</SelectItem>
              <SelectItem value="oldest">Сначала старые</SelectItem>
              <SelectItem value="title_asc">По названию (А→Я)</SelectItem>
              <SelectItem value="title_desc">По названию (Я→А)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(q || mediaType !== "all" || tag || productId !== "all" || zoneFilter !== "all" || sort !== "newest") && (
          <button onClick={resetFilters} className="text-xs text-espresso underline self-start"
                  data-testid="media-filter-reset">Сбросить фильтры</button>
        )}
      </div>

      <div className="text-xs text-muted-foreground mb-2" data-testid="media-count">
        Найдено: {items.length}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic text-center py-8">
          Ничего не найдено.
        </p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="media-list">
          {items.map((m) => {
            const displayTitle = m.title || m.original_filename || "(без названия)";
            const typeLabel = m.media_type ? MEDIA_TYPE_LABEL[m.media_type] || m.media_type : null;
            const product = m.product_id ? products.find((p) => p.id === m.product_id) : null;
            return (
              <li key={m.id} className="rounded-2xl bg-white border hairline overflow-hidden flex flex-col" data-testid={`media-item-${m.id}`}>
                <div className="aspect-square bg-sand/30 flex items-center justify-center overflow-hidden">
                  {m.file_type === "image" && m.file_url ? (
                    <AdminMediaThumbnail url={m.file_url} alt={m.alt_text || displayTitle} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-espresso/40" />
                  )}
                </div>
                <div className="p-2 flex-1 flex flex-col">
                  <div className="text-sm text-espresso truncate" title={displayTitle}>{displayTitle}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {m.access_zone && <span className="chip bg-sand/60 text-espresso text-[10px]">{m.access_zone === "paid" ? "Закрытое" : "Открытое"}</span>}
                    {!m.access_zone && <span className="chip bg-sand/40 text-espresso/65 text-[10px]">Архив</span>}
                    {typeLabel && (
                      <span className="chip bg-sand/60 text-espresso text-[10px]" data-testid={`media-type-badge-${m.id}`}>{typeLabel}</span>
                    )}
                    {product && (
                      <span className="chip bg-blue-50 text-blue-900 text-[10px]" title={product.title}>{product.title}</span>
                    )}
                  </div>
                  {(m.tags || []).length > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-1 truncate" title={(m.tags || []).join(", ")}>
                      #{(m.tags || []).join(" #")}
                    </div>
                  )}
                  {m.alt_text && (
                    <div className="text-[10px] text-muted-foreground mt-1 truncate italic" title={m.alt_text}>alt: {m.alt_text}</div>
                  )}
                  <div className="flex gap-1 mt-auto pt-2">
                    <button onClick={() => setEditing(m)} className="p-1 rounded-full hover:bg-sand/50" title="Изменить" data-testid={`media-edit-${m.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-espresso" />
                    </button>
                    <button onClick={() => requestDelete(m)} className="p-1 rounded-full hover:bg-red-50" title="Удалить" data-testid={`media-delete-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <MediaEditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        data={editing}
        products={products}
        onSave={save}
      />
      <MediaDeleteDialog
        open={!!confirmDelete}
        payload={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={performDelete}
      />
    </div>
  );
}

function MediaEditDialog({ open, onClose, data, products, onSave }) {
  const [form, setForm] = useState({});
  const [usage, setUsage] = useState(null);
  const [sections, setSections] = useState([]);
  useEffect(() => {
    setForm(data || {});
    setUsage(null);
    if (data?.id) {
      api.get(`/admin/media/${data.id}/usage`).then((r) => setUsage(r.data)).catch(() => setUsage(null));
    }
  }, [data]);
  useEffect(() => {
    if (!open) return;
    const pid = form.product_id;
    if (!pid) { setSections([]); return; }
    api.get(`/admin/sections?product_id=${pid}`)
      .then((r) => setSections(r.data.filter((s) => s.product_id === pid)))
      .catch(() => setSections([]));
  }, [open, form.product_id]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="media-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-espresso">Медиа</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Метаданные файла: название, тип, теги, привязки и места использования.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {form.file_url && form.file_type === "image" && (
            <img src={form.file_url} alt="" className="w-full max-h-56 object-contain rounded-2xl border hairline bg-sand/20" />
          )}
          {form.original_filename && (
            <div className="text-[11px] text-muted-foreground truncate" data-testid="media-filename">
              Файл: {form.original_filename}
            </div>
          )}
          <Field label="Название" v={form.title} onChange={(v) => set("title", v)} testid="media-title" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Тип"
              v={form.media_type || "__none__"}
              onChange={(v) => set("media_type", v === "__none__" ? null : v)}
              testid="media-media-type"
              options={[["__none__", "Не выбрано"], ...MEDIA_TYPES]}
            />
            <SelectField
              label="Продукт"
              v={form.product_id || "__none__"}
              onChange={(v) => set("product_id", v === "__none__" ? null : v)}
              testid="media-product-id"
              options={[["__none__", "Не привязан"], ...products.map((p) => [p.id, p.title])]}
            />
          </div>
          {form.product_id && sections.length > 0 && (
            <SelectField
              label="Раздел (опционально)"
              v={form.section_id || "__none__"}
              onChange={(v) => set("section_id", v === "__none__" ? null : v)}
              testid="media-section-id"
              options={[["__none__", "Не привязан"], ...sections.map((s) => [s.id, s.title])]}
            />
          )}
          <Field label="Alt-текст" v={form.alt_text} onChange={(v) => set("alt_text", v)} testid="media-alt" />
          <Field label="Теги (через запятую)"
            v={(form.tags || []).join(", ")}
            onChange={(v) => set("tags", v.split(",").map((t) => t.trim()).filter(Boolean))}
            testid="media-tags" />
          <Field label="Описание" v={form.description} onChange={(v) => set("description", v)}
                 testid="media-description" multiline />

          <div className="rounded-2xl border hairline bg-sand/20 p-3" data-testid="media-usage-block">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Где используется</div>
            {usage === null ? (
              <div className="text-xs text-muted-foreground">Загрузка…</div>
            ) : usage.count === 0 && (usage.warnings || []).length > 0 ? (
              <div className="space-y-1" data-testid="media-usage-unknown">
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  Полная проверка связей невозможна для этого файла. Удаление заблокировано до замены или повторной загрузки.
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Технические детали: {(usage.warnings || []).join(", ")}
                </div>
              </div>
            ) : usage.count === 0 ? (
              <div className="text-xs text-muted-foreground italic" data-testid="media-usage-empty">
                Файл нигде не используется. Удаление разрешено.
              </div>
            ) : (
              <ul className="text-sm text-espresso space-y-1" data-testid="media-usage-list">
                {usage.locations.map((l, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="chip bg-white text-espresso border hairline text-[10px]">{USAGE_KIND_LABEL[l.kind] || l.kind}</span>
                    <span className="truncate">{l.title || l.slug || l.id}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Отмена</Button>
          <Button onClick={() => onSave(form)} className="btn-pill bg-espresso text-white" data-testid="media-save">Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaDeleteDialog({ open, payload, onCancel, onConfirm }) {
  if (!open || !payload) return null;
  const { media, usage } = payload;
  const warnings = usage?.warnings || [];
  const usageIncomplete = warnings.length > 0;
  const inUse = (usage?.count || 0) > 0;
  // "Unknown" = we couldn't fully check body references (legacy data-URI or oversized URL).
  // Treated as NOT SAFE TO DELETE, distinct from the "in use" state.
  const unknown = !inUse && usageIncomplete;
  const canDelete = !inUse && !unknown;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="media-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-espresso">
            Удалить «{media.title || media.original_filename || "медиа"}»?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {inUse
              ? "Файл используется в других сущностях — удаление заблокировано."
              : unknown
                ? "Полная проверка связей невозможна — удаление заблокировано."
                : "Файл нигде не используется — удаление безопасно."}
          </DialogDescription>
        </DialogHeader>
        {inUse ? (
          <div className="space-y-2">
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="media-delete-blocked">
              Этот файл используется и не может быть удалён, пока связи не будут удалены или заменены.
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground pt-1">Где используется ({usage.count})</div>
            <ul className="text-sm text-espresso space-y-1 max-h-48 overflow-y-auto" data-testid="media-delete-usage-list">
              {usage.locations.map((l, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="chip bg-sand/60 text-espresso text-[10px]">{USAGE_KIND_LABEL[l.kind] || l.kind}</span>
                  <span className="truncate">{l.title || l.slug || l.id}</span>
                </li>
              ))}
            </ul>
            <div className="text-xs text-muted-foreground">
              Сначала уберите или замените эти ссылки — затем удаление станет доступным.
            </div>
          </div>
        ) : unknown ? (
          <div className="space-y-2" data-testid="media-delete-unknown">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="media-delete-unknown-message">
              Для этого старого файла невозможно полностью проверить все связи. Удаление заблокировано до замены или повторной загрузки файла.
            </div>
            <div className="text-[11px] text-muted-foreground" data-testid="media-delete-unknown-warnings">
              Технические детали: {warnings.join(", ")}
            </div>
          </div>
        ) : (
          <div className="text-sm text-espresso" data-testid="media-delete-safe">
            Файл нигде не используется. Удаление безопасно.
          </div>
        )}
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onCancel} className="btn-pill" data-testid="media-delete-cancel">
            {canDelete ? "Отмена" : "Закрыть"}
          </Button>
          {canDelete && (
            <Button
              onClick={() => onConfirm(false)}
              className="btn-pill bg-espresso text-white"
              data-testid="media-delete-confirm"
            >Удалить</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = q.trim()
        ? await api.get(`/admin/media/search?q=${encodeURIComponent(q.trim())}`)
        : await api.get("/admin/media");
      setItems(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  useEffect(() => { if (open) { const t = setTimeout(load, 250); return () => clearTimeout(t); } }, [q]); // eslint-disable-line

  const upload = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.replace(/\.[^.]+$/, ""));
      form.append("access_zone", "public");
      const { data } = await api.post("/admin/media/upload-file", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Загружено");
      onPick(data.file_url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">Медиатека</DialogTitle></DialogHeader>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Поиск" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl h-10 bg-white" data-testid="media-picker-search" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
                 onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <Button onClick={() => fileRef.current?.click()} className="btn-pill bg-espresso text-white h-10 px-3 text-sm" disabled={uploading} data-testid="media-picker-upload">
            <Upload className="w-4 h-4 mr-1" /> {uploading ? "…" : "Загрузить"}
          </Button>
        </div>
        <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {items.filter((m) => m.access_zone !== "paid").map((m) => (
            <li key={m.id}>
              <button
                onClick={() => onPick(m.file_url)}
                className="w-full aspect-square rounded-xl overflow-hidden border hairline hover:ring-2 hover:ring-espresso"
                data-testid={`media-pick-${m.id}`}
                title={m.title}
              >
                {m.file_type === "image" && m.file_url ? (
                  <AdminMediaThumbnail url={m.file_url} alt={m.alt_text || m.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-sand/40 flex items-center justify-center"><ImageIcon className="w-6 h-6 text-espresso/40" /></div>
                )}
              </button>
            </li>
          ))}
          {items.length === 0 && <li className="text-sm text-muted-foreground italic col-span-full py-6 text-center">Ничего не найдено</li>}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Relations (owner-facing overview) ----------
function RelationsPanel() {
  const [materials, setMaterials] = useState([]);
  const [selected, setSelected] = useState("");
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [rel, setRel] = useState({ products: [], sections: [] });

  useEffect(() => {
    (async () => {
      try {
        const [m, p, s] = await Promise.all([api.get("/admin/materials"), api.get("/admin/products"), api.get("/admin/sections")]);
        setMaterials(m.data); setProducts(p.data); setSections(s.data);
        if (m.data[0]) setSelected(m.data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);
  const loadRel = async (mid) => {
    if (!mid) return;
    try { const { data } = await api.get(`/admin/materials/${mid}/relations`); setRel(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadRel(selected); }, [selected]);

  const attachProduct = async (pid) => { try { await api.post(`/admin/products/${pid}/materials`, { material_id: selected, sort_order: 0 }); loadRel(selected); toast.success("Привязано"); } catch (e) { toast.error(formatApiError(e)); } };
  const detachProduct = async (pid) => { try { await api.delete(`/admin/products/${pid}/materials/${selected}`); loadRel(selected); } catch (e) { toast.error(formatApiError(e)); } };
  const attachSection = async (sid) => { try { await api.post(`/admin/sections/${sid}/materials`, { material_id: selected, sort_order: 0 }); loadRel(selected); toast.success("Привязано"); } catch (e) { toast.error(formatApiError(e)); } };
  const detachSection = async (sid) => { try { await api.delete(`/admin/sections/${sid}/materials/${selected}`); loadRel(selected); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3" data-testid="admin-relations">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <label className="text-xs uppercase tracking-widest text-muted-foreground">Материал</label>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="h-10 rounded-xl sm:w-96" data-testid="relations-material-select">
            <SelectValue placeholder="Выберите материал" />
          </SelectTrigger>
          <SelectContent>
            {materials.map((m) => (<SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border hairline bg-white p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Продукты</div>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="relations-linked-products">
          {rel.products.map((p) => (
            <span key={p.id} className="chip bg-sand/60 text-espresso flex items-center gap-1">
              {p.title}
              <button onClick={() => detachProduct(p.id)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {rel.products.length === 0 && <span className="text-xs text-muted-foreground italic">Пока не привязан</span>}
        </div>
        <Select value="" onValueChange={attachProduct}>
          <SelectTrigger className="h-9 rounded-xl" data-testid="relations-attach-product"><SelectValue placeholder="+ Привязать продукт" /></SelectTrigger>
          <SelectContent>
            {products.filter((p) => !rel.products.find((x) => x.id === p.id)).map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border hairline bg-white p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Разделы</div>
        <div className="flex flex-wrap gap-2 mb-2" data-testid="relations-linked-sections">
          {rel.sections.map((s) => {
            const p = products.find((x) => x.id === s.product_id);
            return (
              <span key={s.id} className="chip bg-sand/60 text-espresso flex items-center gap-1">
                {p ? `${p.title} → ${s.title}` : s.title}
                <button onClick={() => detachSection(s.id)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
              </span>
            );
          })}
          {rel.sections.length === 0 && <span className="text-xs text-muted-foreground italic">Пока не привязан</span>}
        </div>
        <Select value="" onValueChange={attachSection}>
          <SelectTrigger className="h-9 rounded-xl" data-testid="relations-attach-section"><SelectValue placeholder="+ Привязать раздел" /></SelectTrigger>
          <SelectContent>
            {sections.filter((s) => !rel.sections.find((x) => x.id === s.id)).map((s) => {
              const p = products.find((x) => x.id === s.product_id);
              return <SelectItem key={s.id} value={s.id}>{p ? `${p.title} → ${s.title}` : s.title}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ---------- Users ----------
function UsersPanel() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openUser, setOpenUser] = useState(null);
  const [products, setProducts] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data || [])).catch(() => {}); }, []);
  const setRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}`, { role }); toast.success("Роль обновлена"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setScope = async (uid, values) => {
    try { await api.patch(`/admin/users/${uid}`, { product_scope_ids: values }); toast.success("Область куратора обновлена"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((u) => {
      const st = u.account_status || "active";
      if (statusFilter === "active" && st !== "active") return false;
      if (statusFilter === "blocked" && st !== "blocked") return false;
      if (statusFilter === "deleted" && st !== "deleted") return false;
      if (!s) return true;
      return (u.email || "").toLowerCase().includes(s) || (u.display_name || "").toLowerCase().includes(s);
    });
  }, [items, q, statusFilter]);
  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap gap-2 mb-3">
        {[
          ["all", "Все"], ["active", "Активные"], ["blocked", "Заблокированные"], ["deleted", "Удалённые"],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setStatusFilter(v)}
            data-testid={`users-status-filter-${v}`}
            className={`chip px-3 py-1 border text-xs uppercase tracking-widest transition ${
              statusFilter === v ? "bg-espresso text-white border-espresso" : "bg-white text-espresso border-espresso/25 hover:bg-sand/40"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input placeholder="Поиск по email или имени" value={q} onChange={(e) => setQ(e.target.value)}
               className="pl-9 rounded-xl h-10 bg-white" data-testid="users-search" />
      </div>
      <ul className="space-y-2" data-testid="admin-users-list">
        {filtered.map((u) => {
          const st = u.account_status || "active";
          const badge = st === "active" ? { label: "Активен", cls: "bg-[#E3F0D3] text-[#3B4A2A] border-[#B8C7A0]" }
                       : st === "blocked" ? { label: "Заблокирован", cls: "bg-[#F7E1DA] text-[#7A2E1F] border-[#E5B5A8]" }
                       : { label: "Удалён", cls: "bg-[#E6E6E6] text-[#565656] border-[#CFCFCF]" };
          return (
          <li key={u.id} className="rounded-2xl bg-white border hairline p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-user-${u.id}`}>
            <button onClick={() => setOpenUser(u.id)} className="flex-1 min-w-0 text-left" data-testid={`admin-user-open-${u.id}`}>
              <div className="font-medium text-espresso truncate">{u.display_name || u.email}</div>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
            </button>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${badge.cls}`} data-testid={`user-status-${u.id}`}>{badge.label}</span>
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-espresso/20 bg-sand/35 text-espresso"
              data-testid={`user-jurisdiction-${u.id}`}
              title="Региональный контур аккаунта"
            >
              {u.jurisdiction || "Не назначен"}
            </span>
            <Select value={u.role} onValueChange={(v) => setRole(u.id, v)} disabled={st === "deleted" || u.role === "owner"}>
              <SelectTrigger className="w-32 h-9 rounded-xl" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Читатель</SelectItem>
                <SelectItem value="curator">Куратор</SelectItem>
                {currentUser?.role === "owner" && <SelectItem value="administrator">Администратор</SelectItem>}
                {u.role === "owner" && <SelectItem value="owner">Владелец</SelectItem>}
              </SelectContent>
            </Select>
            {u.role === "curator" && <select multiple value={u.product_scope_ids || []}
              onChange={(event) => setScope(u.id, Array.from(event.target.selectedOptions, (option) => option.value))}
              className="min-w-48 min-h-16 rounded-xl border hairline bg-white p-2 text-xs" aria-label="Программы куратора" data-testid={`curator-scope-${u.id}`}>
              {products.map((product) => <option key={product.id} value={product.id}>{product.title}</option>)}
            </select>}
          </li>
          );
        })}
      </ul>
      <UserDetailDialog open={!!openUser} uid={openUser} onClose={() => setOpenUser(null)} currentUserId={currentUser?.id} onChanged={load} />
    </div>
  );
}

function UserDetailDialog({ open, uid, onClose, currentUserId, onChanged }) {
  const [data, setData] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = async () => {
    if (!uid) return;
    try { const { data: d } = await api.get(`/admin/users/${uid}`); setData(d); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    if (!open || !uid) return;
    setBlockOpen(false); setDeleteOpen(false); setReason(""); setConfirmText("");
    reload();
  }, [open, uid]); // eslint-disable-line

  const u = data?.user;
  const isSelf = u && currentUserId && u.id === currentUserId;
  const status = u?.account_status || "active";

  const doBlock = async () => {
    const r = reason.trim();
    if (!r) { toast.error("Укажите причину"); return; }
    setBusy(true);
    try { await api.post(`/admin/users/${uid}/block`, { reason: r }); toast.success("Пользователь заблокирован"); setBlockOpen(false); await reload(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doUnblock = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${uid}/unblock`); toast.success("Разблокирован"); await reload(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "УДАЛИТЬ") { toast.error("Введите УДАЛИТЬ"); return; }
    setBusy(true);
    try { await api.delete(`/admin/users/${uid}`); toast.success("Аккаунт удалён"); setDeleteOpen(false); await reload(); onChanged?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const badge = status === "active" ? "Активен" : status === "blocked" ? "Заблокирован" : "Удалён";

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">Карточка пользователя</DialogTitle></DialogHeader>
          {!data ? (
            <p className="text-sm text-muted-foreground font-serif italic py-4 text-center">Загрузка…</p>
          ) : (
            <div className="space-y-2 text-sm">
              <Row k="Email" v={u.email} />
              <Row k="Имя" v={u.display_name || "—"} />
              <Row k="Роль" v={roleLabel(u.role)} />
              <Row
                k="Контур"
                v={u.jurisdiction === "RU" ? "RU" : u.jurisdiction === "INTERNATIONAL" ? "International" : "Не назначен"}
              />
              <Row k="Статус аккаунта" v={badge} />
              {status === "blocked" && (
                <>
                  <Row k="Дата блокировки" v={u.blocked_at ? new Date(u.blocked_at).toLocaleString("ru-RU") : "—"} />
                  <Row k="Причина" v={u.block_reason || "—"} />
                </>
              )}
              <Row k="Активные доступы" v={data.active_grants.length} />
              {data.active_grants.length > 0 && (
                <ul className="pl-3 space-y-1 text-xs">
                  {data.active_grants.map((g) => (
                    <li key={g.id}>
                      · {g.product_title || g.product_id} ({g.access_type === "lifetime" ? "Бессрочно" : "Ограниченный период"})
                    </li>
                  ))}
                </ul>
              )}
              <Row k="Сохранённых материалов" v={data.saved_materials_count} />
              <Row k="Проектов Архитектора десертов" v={data.constructions_count} />
              <Row k="Платежей" v={(data.payments || []).length} />
              {(data.payments || []).map((payment) => <div key={payment.id} className="rounded-xl bg-sand/30 p-2 text-xs">
                {payment.product_title || payment.product_id} · {(payment.amount_minor / 100).toFixed(2)} {payment.currency} · {payment.status}
              </div>)}

              {!isSelf && u.role !== "owner" && status !== "deleted" && (
                <div className="pt-3 flex flex-wrap gap-2">
                  {status === "active" ? (
                    <Button onClick={() => setBlockOpen(true)} data-testid="user-block-btn"
                      className="btn-pill min-h-[36px] bg-[#F2EADF] hover:bg-[#EADFCF] text-espresso border border-espresso/25 shadow-none">
                      Заблокировать
                    </Button>
                  ) : (
                    <Button onClick={doUnblock} disabled={busy} data-testid="user-unblock-btn"
                      className="btn-pill min-h-[36px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none">
                      Разблокировать
                    </Button>
                  )}
                  <Button onClick={() => setDeleteOpen(true)} variant="outline" data-testid="user-delete-btn"
                    className="btn-pill min-h-[36px] text-red-600 border-red-200 hover:bg-red-50">
                    Удалить аккаунт
                  </Button>
                </div>
              )}
              {isSelf && (
                <p className="pt-3 text-[11px] text-muted-foreground italic">Собственный аккаунт нельзя заблокировать или удалить.</p>
              )}
              {u.role === "owner" && !isSelf && <p className="pt-3 text-[11px] text-muted-foreground italic">Владелец защищён от блокировки, удаления и смены роли.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={blockOpen} onOpenChange={(o) => !o && !busy && setBlockOpen(false)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="user-block-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg text-espresso">Заблокировать пользователя?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Пользователь потеряет доступ к платформе, но его покупки, доступы и история сохранятся.</p>
          <div className="mt-2">
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Причина блокировки</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
              placeholder="Например: спам, оскорбления, нарушение правил…" data-testid="user-block-reason" disabled={busy} />
          </div>
          <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
            <Button variant="outline" className="btn-pill min-h-[36px]" onClick={() => setBlockOpen(false)} disabled={busy}>Отмена</Button>
            <Button onClick={doBlock} disabled={busy || !reason.trim()} data-testid="user-block-confirm"
              className="btn-pill min-h-[36px] bg-[#F2EADF] hover:bg-[#EADFCF] text-espresso border border-espresso/25 shadow-none">
              {busy ? "Блокируем…" : "Заблокировать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={(o) => !o && !busy && setDeleteOpen(false)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="user-delete-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg text-espresso">Удалить аккаунт пользователя?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Доступ будет прекращён. Финансовая и системная история, которую необходимо сохранить, не будет удалена.</p>
          <div className="mt-2">
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Введите УДАЛИТЬ для подтверждения</label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="mt-1 h-10 rounded-xl" data-testid="user-delete-confirm-input" disabled={busy} />
          </div>
          <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
            <Button variant="outline" className="btn-pill min-h-[36px]" onClick={() => setDeleteOpen(false)} disabled={busy}>Отмена</Button>
            <Button onClick={doDelete} disabled={busy || confirmText.trim().toUpperCase() !== "УДАЛИТЬ"} data-testid="user-delete-confirm"
              className="btn-pill min-h-[36px] bg-red-600 hover:bg-red-700 text-white border border-red-700 shadow-none">
              {busy ? "Удаляем…" : "Удалить аккаунт"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-espresso font-medium text-right">{String(v)}</span>
    </div>
  );
}

// ---------- Access ----------
function AccessPanel() {
  const { items, editing, setEditing, save, remove } = useCrud("/admin/access-grants");
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([api.get("/admin/users"), api.get("/admin/products")]);
        setUsers(u.data); setProducts(p.data);
      } catch { /* noop */ }
    })();
  }, []);

  const describe = (it) => {
    // Status: Активен / Отозван / Истёк
    let statusLabel = it.status === "active" ? "Активен" : "Отозван";
    if (it.status === "active" && it.access_type === "fixed_period" && it.end_at) {
      const end = new Date(it.end_at);
      if (!Number.isNaN(end.getTime()) && end < new Date()) statusLabel = "Истёк";
    }
    const duration = it.access_type === "lifetime"
      ? "Бессрочно"
      : (it.end_at ? `до ${new Date(it.end_at).toLocaleDateString("ru-RU")}` : "Ограниченный период");
    return { statusLabel, duration };
  };

  return (
    <div data-testid="admin-access">
      <PanelHeader title="Доступы"
        onNew={() => setEditing({ access_type: "lifetime", source: "manual", status: "active" })}
        testid="admin-new-access" />
      <ItemList items={items} onEdit={setEditing} onDelete={remove} testidPrefix="admin-access-item"
        renderTitle={(it) => {
          const u = users.find((x) => x.id === it.user_id);
          const p = products.find((x) => x.id === it.product_id);
          return `${u?.email || it.user_id} → ${p?.title || it.product_id}`;
        }}
        renderSub={(it) => {
          const { statusLabel, duration } = describe(it);
          return `${statusLabel} · ${duration} · источник: ${it.source}`;
        }}
      />
      <AccessEditDialog open={!!editing} onClose={() => setEditing(null)} data={editing} onSave={save} users={users} products={products} />
      <ProfessionalMapComplimentaryAccess users={users} />
    </div>
  );
}


function ProfessionalMapComplimentaryAccess({ users }) {
  const [invites, setInvites] = useState([]);
  const [pending, setPending] = useState([]);
  const [userId, setUserId] = useState("");
  const [days, setDays] = useState("30");
  const [rejectingId, setRejectingId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endpoint = "/admin/professional-map/complimentary";
  async function refresh() {
    const [a,b] = await Promise.all([
      api.get(endpoint), api.get("/admin/professional-map/complimentary-review/pending"),
    ]);
    setInvites(a.data || []);
    setPending(b.data || []);
  }
  useEffect(() => { refresh().catch(() => setError("Не удалось загрузить промо-размещения.")); }, []);
  async function run(action) {
    setBusy(true); setError("");
    try { await action(); await refresh(); }
    catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  }
  const displayUser = (id) => users.find((u) => u.id === id)?.email || id;
  return (
    <section className="mt-6 space-y-4 rounded-2xl border hairline bg-white p-4"
      data-testid="pm-complimentary-admin">
      <h3 className="font-serif text-xl text-espresso">Профессиональная карта · бесплатное размещение</h3>
      <p className="text-sm text-muted-foreground">
        Приглашение не публикует страницу автоматически. После согласия участника и проверки
        администратором начинается срок размещения. Автоматических списаний нет.
      </p>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_110px_auto] sm:items-end">
        <label className="space-y-1 text-xs">Пользователь
          <select value={userId} onChange={(e)=>setUserId(e.target.value)}
            className="block h-11 w-full rounded-xl border border-input bg-white px-3 text-sm"
            data-testid="pm-promo-user">
            <option value="">Выбрать пользователя</option>
            {users.map((u)=><option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-xs">Дней
          <input type="number" min="1" max="365" value={days} onChange={(e)=>setDays(e.target.value)}
            className="block h-11 w-full rounded-xl border border-input px-3 text-sm"
            data-testid="pm-promo-duration"/>
        </label>
        <button type="button" disabled={busy || !userId || !Number.isInteger(Number(days)) ||
          Number(days)<1 || Number(days)>365} className="btn-pill min-h-11 bg-[#F4EADD] px-4 text-sm disabled:opacity-50"
          data-testid="pm-promo-issue" onClick={()=>run(()=>api.post(endpoint,{
            user_id:userId, duration_days:Number(days),
          }))}>Выдать приглашение</button>
      </div>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <div className="space-y-2">
        {invites.map((invite)=>(
          <div key={invite.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3"
            data-testid="pm-promo-entry">
            <div className="text-sm">
              <strong>{displayUser(invite.user_id)}</strong>
              <div className="text-xs text-muted-foreground">
                {invite.state === "issued" ? "Ожидает публикации"
                  : invite.state === "revoked" ? "Отозвано"
                  : invite.expires_at && new Date(invite.expires_at) <= new Date() ? "Срок истёк"
                  : "Размещение активно"}
                {" · "}{invite.duration_days} дней
                {invite.expires_at ? " · до "+new Date(invite.expires_at).toLocaleDateString("ru-RU") : ""}
              </div>
            </div>
            {invite.state === "issued" && <button type="button" disabled={busy}
              className="rounded-lg border px-3 py-2 text-xs"
              onClick={()=>run(()=>api.patch(endpoint+"/"+encodeURIComponent(invite.id),{
                duration_days:Number(days),
              }))}>Изменить срок</button>}
            {["issued","active"].includes(invite.state) && <button type="button"
              className="rounded-lg border px-3 py-2 text-xs" disabled={busy}
              onClick={()=>run(()=>api.post(endpoint+"/"+encodeURIComponent(invite.id)+"/revoke"))}>
              Отозвать</button>}
          </div>
        ))}
      </div>
      {pending.length > 0 && <div className="space-y-2" data-testid="pm-promo-pending">
        <h4 className="font-serif text-lg">Ожидают подтверждения</h4>
        {pending.map((item)=>(
          <div key={item.card_id} className="space-y-3 rounded-xl border p-3 text-sm"
            data-testid="pm-promo-review-item">
            <div>
              <strong>{item.public_name}</strong> · {displayUser(item.user_id)}
              <div className="text-xs text-muted-foreground">
                {[item.city, item.country].filter(Boolean).join(", ")}
                {item.specializations?.length ? " · " + item.specializations.join(" · ") : ""}
              </div>
              {item.description && <p className="mt-2 whitespace-pre-wrap text-sm text-[#665344]">
                {item.description}
              </p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={busy} className="rounded-lg border bg-[#F4EADD] px-3 py-2"
                onClick={()=>run(()=>api.post(
                  "/admin/professional-map/complimentary-review/"+encodeURIComponent(item.invitation_id)+"/approve",
                  {expected_revision:item.revision}
                ))}>Подтвердить публикацию</button>
              <button type="button" disabled={busy} className="rounded-lg border px-3 py-2"
                onClick={()=>{setRejectingId(rejectingId===item.card_id?"":item.card_id);setRejectReason("");}}>
                Отклонить с причиной
              </button>
            </div>
            {rejectingId===item.card_id && (
              <div className="space-y-2">
                <label className="block text-xs">Что необходимо исправить?
                  <textarea rows={3} maxLength={500} value={rejectReason}
                    onChange={(e)=>setRejectReason(e.target.value)}
                    data-testid="pm-promo-reject-reason"
                    className="mt-1 w-full rounded-xl border border-input p-3 text-sm"/>
                </label>
                <button type="button" disabled={busy || rejectReason.trim().length<5}
                  className="rounded-lg border px-3 py-2 disabled:opacity-50"
                  data-testid="pm-promo-reject"
                  onClick={()=>run(async()=>{
                    await api.post(
                      "/admin/professional-map/complimentary-review/"+encodeURIComponent(item.invitation_id)+"/reject",
                      {expected_revision:item.revision, reason:rejectReason.trim()}
                    );
                    setRejectingId("");setRejectReason("");
                  })}>Отправить на доработку</button>
              </div>
            )}
          </div>
        ))}
      </div>}
    </section>
  );
}

// ---------- Shared UI primitives ----------
function ItemList({ items, onEdit, onDelete, onUp, onDown, onRowClick, renderAction, testidPrefix, renderTitle, renderSub }) {
  if (items.length === 0) {
    return <p className="text-center text-sm text-muted-foreground py-8 font-serif italic">Пока пусто</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it, idx) => (
        <li key={it.id} className="rounded-2xl bg-white border hairline p-3 flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-${it.id}`}>
          {onRowClick ? (
            <button
              type="button"
              onClick={() => onRowClick(it)}
              data-testid={`${testidPrefix}-row-${it.id}`}
              className="flex-1 min-w-0 basis-full sm:basis-0 text-left hover:bg-sand/20 rounded-xl px-2 py-1 -mx-1 cursor-pointer"
            >
              <div className="font-medium text-espresso truncate">{renderTitle(it)}</div>
              <div className="text-xs text-muted-foreground truncate">{renderSub(it)}</div>
            </button>
          ) : (
            <div className="flex-1 min-w-0 basis-full sm:basis-0">
              <div className="font-medium text-espresso truncate">{renderTitle(it)}</div>
              <div className="text-xs text-muted-foreground truncate">{renderSub(it)}</div>
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto flex-wrap justify-end">
          {renderAction?.(it)}
          {onUp && (
            <button onClick={() => onUp(it.id)} disabled={idx === 0} data-testid={`${testidPrefix}-up-${it.id}`} className="p-2 rounded-full hover:bg-sand/50 disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"><ChevronUp className="w-4 h-4 text-espresso" /></button>
          )}
          {onDown && (
            <button onClick={() => onDown(it.id)} disabled={idx === items.length - 1} data-testid={`${testidPrefix}-down-${it.id}`} className="p-2 rounded-full hover:bg-sand/50 disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"><ChevronDown className="w-4 h-4 text-espresso" /></button>
          )}
          <button onClick={() => onEdit(it)} data-testid={`${testidPrefix}-edit-${it.id}`} className="p-2 rounded-full hover:bg-sand/50 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Редактировать"><Pencil className="w-4 h-4 text-espresso" /></button>
          <button onClick={() => onDelete(it.id)} data-testid={`${testidPrefix}-delete-${it.id}`} className="p-2 rounded-full hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Удалить"><Trash2 className="w-4 h-4 text-red-500" /></button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PanelHeader({ onNew, title, testid }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="font-serif text-xl text-espresso">{title}</h2>
      <Button onClick={onNew} className="btn-pill bg-espresso hover:bg-espressoDark text-white h-auto py-2 px-4 text-sm" data-testid={testid}>
        <Plus className="w-4 h-4 mr-1" /> Создать
      </Button>
    </div>
  );
}

function ProductEditDialog({ open, onClose, data, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(data || {}); }, [data]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">{form.id ? "Редактирование продукта" : "Новый продукт"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Название" v={form.title} onChange={(v) => set("title", v)} testid="product-title" />
          <Field label="Slug" v={form.slug} onChange={(v) => set("slug", v)} testid="product-slug" />
          <SelectField label="Тип" v={form.type} onChange={(v) => set("type", v)} testid="product-type" options={PRODUCT_TYPES} />
          <SelectField label="Статус" v={form.status} onChange={(v) => set("status", v)} testid="product-status" options={STATUS_OPTIONS} />
          <Field label="Краткое описание" v={form.short_description} onChange={(v) => set("short_description", v)} testid="product-short" />
          <Field label="Описание" v={form.description} onChange={(v) => set("description", v)} testid="product-desc" multiline />
          <Field label="Обложка (URL)" v={form.cover_image} onChange={(v) => set("cover_image", v)} testid="product-cover" />
          <Field label="Порядок" v={form.sort_order} onChange={(v) => set("sort_order", parseInt(v || "0", 10) || 0)} testid="product-sort" />
          <Checkbox label="Публичный" v={form.is_public} onChange={(v) => set("is_public", v)} testid="product-public" />
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Отмена</Button>
          <Button onClick={() => onSave(form)} className="btn-pill bg-espresso text-white" data-testid="product-save">Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionEditDialog({ open, onClose, data, onSave, products }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(data || {}); }, [data]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">{form.id ? "Редактирование раздела" : "Новый раздел"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <SelectField label="Продукт" v={form.product_id} onChange={(v) => set("product_id", v)} testid="section-product"
            options={products.map((p) => [p.id, p.title])} />
          <Field label="Название" v={form.title} onChange={(v) => set("title", v)} testid="section-title" />
          <Field label="Slug" v={form.slug} onChange={(v) => set("slug", v)} testid="section-slug" />
          <Field label="Описание" v={form.description} onChange={(v) => set("description", v)} testid="section-desc" multiline />
          <SelectField label="Статус" v={form.status} onChange={(v) => set("status", v)} testid="section-status" options={STATUS_OPTIONS} />
          <Field label="Порядок" v={form.sort_order} onChange={(v) => set("sort_order", parseInt(v || "0", 10) || 0)} testid="section-sort" />
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Отмена</Button>
          <Button onClick={() => onSave(form)} className="btn-pill bg-espresso text-white" data-testid="section-save">Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccessEditDialog({ open, onClose, data, onSave, users, products }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(data || {}); }, [data]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-espresso">{form.id ? "Редактирование доступа" : "Новый доступ"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <SelectField label="Пользователь" v={form.user_id} onChange={(v) => set("user_id", v)} testid="access-user" options={users.map((u) => [u.id, u.email])} />
          <SelectField label="Продукт" v={form.product_id} onChange={(v) => set("product_id", v)} testid="access-product" options={products.map((p) => [p.id, p.title])} />
          <SelectField label="Тип доступа" v={form.access_type} onChange={(v) => set("access_type", v)} testid="access-type"
            options={[["lifetime","Бессрочно"],["fixed_period","Ограниченный период"]]} />
          <SelectField label="Статус" v={form.status} onChange={(v) => set("status", v)} testid="access-status"
            options={[["active","Активен"],["revoked","Отозван"]]} />
          <SelectField label="Источник" v={form.source} onChange={(v) => set("source", v)} testid="access-source"
            options={[["purchase","Покупка"],["manual","Ручной"],["promo","Промо"],["vip","VIP"],["subscription","Подписка"]]} />
          {form.access_type === "fixed_period" && (
            <>
              <Field label="Начало (ISO)" v={form.start_at} onChange={(v) => set("start_at", v)} testid="access-start" />
              <Field label="Окончание (ISO)" v={form.end_at} onChange={(v) => set("end_at", v)} testid="access-end" />
            </>
          )}
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Отмена</Button>
          <Button onClick={() => onSave(form)} className="btn-pill bg-espresso text-white" data-testid="access-save">Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, onChange, testid, multiline }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)} rows={3}
          className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm" />
      ) : (
        <Input data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-10 rounded-xl" />
      )}
    </div>
  );
}
function SelectField({ label, v, onChange, testid, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      <Select value={v || ""} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-10 rounded-xl" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>{options.map(([val, lbl]) => (<SelectItem key={val} value={val}>{lbl}</SelectItem>))}</SelectContent>
      </Select>
    </div>
  );
}
function Checkbox({ label, v, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span className="text-sm text-espresso">{label}</span>
    </label>
  );
}

// ---------- Публикация block (prominent) ----------
const STATUS_TONES = {
  draft:     { chip: "bg-amber-100 text-amber-900 border-amber-200",       dot: "bg-amber-500" },
  review:    { chip: "bg-blue-100 text-blue-900 border-blue-200",          dot: "bg-blue-500" },
  published: { chip: "bg-emerald-100 text-emerald-900 border-emerald-200", dot: "bg-emerald-500" },
  hidden:    { chip: "bg-gray-200 text-gray-800 border-gray-300",          dot: "bg-gray-500" },
  scheduled: { chip: "bg-violet-100 text-violet-900 border-violet-200",    dot: "bg-violet-500" },
};

// ---------- Timezone helpers (Europe/Vilnius) ----------
const APP_TZ = "Europe/Vilnius";

function formatVilnius(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: APP_TZ,
      day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

function isScheduled(m) {
  return !!m && m.status !== "published" && !!m.publish_at;
}

function materialSubtitle(m) {
  const type = MATERIAL_TYPE_LABEL[m.material_type] || m.material_type;
  const free = m.is_free ? " · Бесплатно" : "";
  if (isScheduled(m)) return `${type} · Запланировано: ${formatVilnius(m.publish_at)}${free}`;
  return `${type} · ${STATUS_LABEL[m.status] || m.status}${free}`;
}

// Convert local Vilnius date + time (yyyy-mm-dd + HH:MM) into a UTC ISO with 'Z' suffix.
function vilniusDateTimeToUtcIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  // Build wall-clock in Vilnius, then figure the UTC instant it represents by
  // subtracting the current offset for that wall clock in that TZ.
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  if (![y, mo, d, h, mi].every((n) => Number.isFinite(n))) return null;
  // Naive UTC of the wall clock
  const naiveUtcMs = Date.UTC(y, mo - 1, d, h, mi, 0);
  // Determine the offset for that instant in Vilnius. Vilnius = UTC+2 winter / UTC+3 summer.
  const wall = new Date(naiveUtcMs);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TZ,
    hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(wall);
  const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
  const vilnusMs = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  const offsetMs = vilnusMs - naiveUtcMs; // = local - utc for that instant
  const utcInstantMs = naiveUtcMs - offsetMs;
  return new Date(utcInstantMs).toISOString();
}

// Extract yyyy-mm-dd / HH:MM in the Vilnius wall clock from a UTC ISO.
function utcIsoToVilniusInputs(iso) {
  if (!iso) return { date: "", time: "" };
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: APP_TZ, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((p) => p.type === t)?.value || "";
    return { date: `${g("year")}-${g("month")}-${g("day")}`, time: `${g("hour")}:${g("minute")}` };
  } catch { return { date: "", time: "" }; }
}

function publicationExplanation(status, isFree, scheduledIso) {
  if (scheduledIso) return `Публикация запланирована на ${formatVilnius(scheduledIso)} (Europe/Vilnius). До этого материал не виден пользователям.`;
  if (status === "published" && isFree) return "Материал будет виден в Открытой библиотеке.";
  if (status === "published" && !isFree) return "Материал опубликован, но доступен только через связанные продукты и права доступа.";
  if (status === "draft") return "Материал виден только команде и доступен для предпросмотра.";
  if (status === "review") return "Материал на внутренней проверке. Публикация ещё не выполнена.";
  if (status === "hidden") return "Материал не показывается пользователям.";
  return "";
}

function PublicationBlock({
  status, onStatusChange,
  isFree, onFreeChange,
  publishAt,                    // current publish_at (UTC ISO or null)
  onSchedule,                   // (utcIso) => Promise
  onCancelSchedule,             // () => Promise
  onPublishNow,                 // () => Promise
  materialId,                   // for disabling scheduling on non-persisted new materials
}) {
  const scheduled = !!publishAt && status !== "published";
  const effectiveStatusKey = scheduled ? "scheduled" : status;
  const tone = STATUS_TONES[effectiveStatusKey] || STATUS_TONES.draft;
  const chipLabel = scheduled ? "Запланировано" : (STATUS_LABEL[status] || status);

  const [mode, setMode] = useState(scheduled ? "schedule" : "now");
  useEffect(() => { setMode(scheduled ? "schedule" : "now"); /* re-sync when material loaded */ }, [scheduled, materialId]);

  const initInputs = utcIsoToVilniusInputs(publishAt);
  const [date, setDate] = useState(initInputs.date);
  const [time, setTime] = useState(initInputs.time);
  const [error, setError] = useState("");
  useEffect(() => {
    const p = utcIsoToVilniusInputs(publishAt);
    setDate(p.date); setTime(p.time); setError("");
  }, [publishAt]);

  const commitSchedule = async () => {
    if (!date) { setError("Выберите дату публикации"); return; }
    if (!time) { setError("Выберите время публикации"); return; }
    const iso = vilniusDateTimeToUtcIso(date, time);
    if (!iso) { setError("Некорректная дата или время"); return; }
    if (new Date(iso).getTime() <= Date.now() + 15000) {
      setError("Время публикации должно быть в будущем"); return;
    }
    setError("");
    try { await onSchedule(iso); }
    catch (e) { setError(formatApiError(e)); }
  };

  return (
    <section
      data-testid="material-publication-block"
      className="rounded-2xl border hairline bg-sand/30 p-4 sm:p-5"
      aria-label="Публикация"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${tone.dot}`} aria-hidden />
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Публикация</h3>
        <span
          data-testid="material-status-chip"
          className={`ml-auto inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${tone.chip}`}
        >
          {chipLabel}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Статус</label>
          <div className="mt-1">
            <Select value={status} onValueChange={onStatusChange}>
              <SelectTrigger className="h-11 rounded-xl bg-white" data-testid="material-status">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(([val, lbl]) => (
                  <SelectItem key={val} value={val}>{lbl}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col justify-end">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Доступ</label>
          <label
            className="mt-1 flex items-center gap-2 cursor-pointer rounded-xl bg-white border hairline px-3 py-2.5 min-h-[44px]"
            data-testid="material-free-row"
          >
            <input
              type="checkbox"
              checked={!!isFree}
              onChange={(e) => onFreeChange(e.target.checked)}
              data-testid="material-free"
              className="h-4 w-4"
            />
            <span className="text-sm text-espresso">Бесплатно в Открытой библиотеке</span>
          </label>
        </div>
      </div>

      {/* Publication mode switch — only meaningful for non-published materials */}
      {status !== "published" && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Когда опубликовать</div>
          <div className="inline-flex rounded-full bg-white border hairline p-1" role="tablist" aria-label="Публикация">
            <ModeChip
              active={mode === "now"}
              onClick={() => setMode("now")}
              testid="publish-mode-now"
              label="Опубликовать сейчас"
            />
            <ModeChip
              active={mode === "schedule"}
              onClick={() => setMode("schedule")}
              testid="publish-mode-schedule"
              label="Запланировать"
              disabled={!materialId}
              disabledTitle={!materialId ? "Сначала сохраните материал как черновик" : undefined}
            />
          </div>

          {mode === "schedule" && (
            <div className="mt-3 rounded-2xl bg-white border hairline p-3" data-testid="material-schedule-panel">
              {scheduled ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-espresso">
                      Запланировано на <strong data-testid="scheduled-label">{formatVilnius(publishAt)}</strong>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Часовой пояс: Europe/Vilnius. Публикуется автоматически.</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" className="btn-pill" onClick={() => setMode("edit-schedule")} data-testid="schedule-change">Изменить</Button>
                    <Button type="button" variant="outline" className="btn-pill" onClick={onCancelSchedule} data-testid="schedule-cancel">Отменить планирование</Button>
                    <Button type="button" className="btn-pill bg-emerald-700 hover:bg-emerald-800 text-white" onClick={onPublishNow} data-testid="schedule-publish-now">Опубликовать сейчас</Button>
                  </div>
                </div>
              ) : (
                <ScheduleForm date={date} time={time} setDate={setDate} setTime={setTime} error={error} onCommit={commitSchedule} />
              )}
            </div>
          )}

          {mode === "edit-schedule" && (
            <div className="mt-3 rounded-2xl bg-white border hairline p-3" data-testid="material-schedule-panel">
              <ScheduleForm date={date} time={time} setDate={setDate} setTime={setTime} error={error} onCommit={commitSchedule} />
              <button type="button" className="text-xs text-muted-foreground underline mt-2" onClick={() => { const p = utcIsoToVilniusInputs(publishAt); setDate(p.date); setTime(p.time); setError(""); setMode("schedule"); }}>
                Отменить изменение
              </button>
            </div>
          )}
        </div>
      )}

      <p
        data-testid="material-publication-hint"
        className="mt-3 text-sm text-espresso/80"
      >
        {publicationExplanation(status, !!isFree, scheduled ? publishAt : null)}
      </p>
    </section>
  );
}

function ModeChip({ active, onClick, label, testid, disabled, disabledTitle }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-disabled={disabled}
      title={disabled ? disabledTitle : undefined}
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      className={`rounded-xl px-3 py-1.5 text-sm min-h-[36px] whitespace-nowrap transition-colors ${
        active ? "bg-espresso text-white" : "bg-transparent text-espresso hover:bg-sand/40"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {label}
    </button>
  );
}

function ScheduleForm({ date, time, setDate, setTime, error, onCommit }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Дата публикации</label>
          <Input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="schedule-date" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Время (Europe/Vilnius)</label>
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="schedule-time" />
        </div>
        <div className="flex items-end">
          <Button type="button" onClick={onCommit} className="btn-pill bg-espresso hover:bg-espressoDark text-white w-full" data-testid="schedule-save">
            Запланировать
          </Button>
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mt-2" data-testid="schedule-error">{error}</p>}
    </>
  );
}

// ---------- Instructions (internal knowledge base) ----------
// ---------- Support Panel (Sprint 39) ----------
const STAFF_STATUS_LABEL = {
  new: "Новое", in_progress: "В работе",
  waiting_for_user: "Ждём пользователя", closed: "Закрыто",
};
const STAFF_STATUS_TONES = {
  new: "bg-[#F7EDDA] text-[#7A4A1F] border-[#E1CFA8]",
  in_progress: "bg-[#E7DEF7] text-[#4A3374] border-[#CBBCE7]",
  waiting_for_user: "bg-[#FCE7C6] text-[#8A4D14] border-[#F0CE8B]",
  closed: "bg-[#E6E6E6] text-[#565656] border-[#CFCFCF]",
};
function StaffStatusChip({ status, testid }) {
  const cls = STAFF_STATUS_TONES[status] || "bg-sand/60 text-espresso border-sand";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${cls}`} data-testid={testid}>
      {STAFF_STATUS_LABEL[status] || status}
    </span>
  );
}
function StaffAvatar({ src, name, size = 36 }) {
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div
      className="rounded-xl bg-sand/50 overflow-hidden border hairline flex items-center justify-center text-espresso/60 font-serif shrink-0"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initial}
    </div>
  );
}

function SupportPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = status === "all" ? "" : `?status=${status}`;
      const { data } = await api.get(`/staff/requests${q}`);
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  if (openId) {
    return <StaffRequestDetail rid={openId} onBack={() => {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
      load();
    }} />;
  }

  const sorted = [...items].sort((a, b) => {
    const au = (a.staff_unread_count || 0) > 0 ? 1 : 0;
    const bu = (b.staff_unread_count || 0) > 0 ? 1 : 0;
    if (au !== bu) return bu - au;
    return (b.updated_at || "").localeCompare(a.updated_at || "");
  });

  const openRequest = (rid) => {
    const next = new URLSearchParams(searchParams);
    next.set("open", rid);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-3" data-testid="support-panel">
      <div className="flex flex-wrap gap-2" data-testid="support-filter-bar">
        {[
          ["all", "Все"],
          ["new", "Новые"],
          ["in_progress", "В работе"],
          ["waiting_for_user", "Ждём пользователя"],
          ["closed", "Закрытые"],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setStatus(v)}
            data-testid={`support-filter-${v}`}
            className={`chip px-3 py-1 border text-xs uppercase tracking-widest transition ${
              status === v ? "bg-espresso text-white border-espresso" : "bg-white text-espresso border-espresso/25 hover:bg-sand/40"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>
      ) : sorted.length === 0 ? (
        <div className="rounded-3xl bg-white border hairline px-6 py-10 text-center" data-testid="support-empty">
          <p className="font-serif italic text-espresso/70 text-lg">Обращений нет</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="support-list">
          {sorted.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => openRequest(r.id)}
                data-testid={`support-item-${r.id}`}
                className="w-full text-left rounded-2xl bg-white border hairline p-4 hover:bg-sand/20 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-3">
                  <StaffAvatar src={r.user_avatar} name={r.user_name} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-espresso truncate">{r.user_name || r.user_email || "Пользователь"}</span>
                      {(r.staff_unread_count || 0) > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-[#B84A2A] text-white text-[10px]">
                          {r.staff_unread_count}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-espresso truncate">«{r.material_title || "—"}»</div>
                    {r.product_title && (
                      <div className="text-[11px] text-muted-foreground truncate">{r.product_title}</div>
                    )}
                    {r.last_message_preview && (
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.last_message_preview}</div>
                    )}
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {new Date(r.last_message_at || r.updated_at).toLocaleString("ru-RU")}
                      {r.assigned_staff_name && ` · ${r.assigned_staff_name}`}
                    </div>
                  </div>
                  <StaffStatusChip status={r.status} testid={`support-item-status-${r.id}`} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StaffRequestDetail({ rid, onBack }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/staff/requests/${rid}`); setReq(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [rid]); // eslint-disable-line

  const take = async () => {
    try { const { data } = await api.post(`/staff/requests/${rid}/assign`); setReq(data); toast.success("Обращение в работе"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const changeStatus = async (s) => {
    try { const { data } = await api.patch(`/staff/requests/${rid}`, { status: s }); setReq(data); toast.success("Статус обновлён"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    try { const { data } = await api.post(`/staff/requests/${rid}/reply`, { body: trimmed }); setReq(data); setBody(""); toast.success("Ответ отправлен"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (!req) return <div className="rounded-3xl bg-white border hairline p-6 text-center">Обращение не найдено.</div>;

  const isClosed = req.status === "closed";
  const openMaterial = () => {
    if (req.material_slug) window.open(`/library/${encodeURIComponent(req.material_slug)}`, "_blank");
  };

  return (
    <div className="space-y-4" data-testid="staff-request-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onBack} data-testid="staff-request-back" className="chip bg-white border border-espresso/25 text-espresso px-3 py-1.5 hover:bg-sand/40 inline-flex items-center gap-1.5">
          <ChevronLeft className="w-3.5 h-3.5" /> К списку
        </button>
        <StaffStatusChip status={req.status} testid="staff-request-status" />
      </div>

      <div className="rounded-3xl bg-white border hairline p-4 space-y-3">
        <div className="flex items-center gap-3">
          <StaffAvatar src={req.user_avatar} name={req.user_name} size={44} />
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg text-espresso truncate" data-testid="staff-request-user-name">{req.user_name || req.user_email}</div>
            <div className="text-xs text-muted-foreground truncate">{req.user_email}</div>
          </div>
        </div>
        <div className="border-t hairline pt-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Материал</div>
          <div className="font-serif text-espresso mt-0.5" data-testid="staff-request-material">{req.material_title || "—"}</div>
          {(req.product_title || req.section_title) && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {req.product_title}{req.section_title ? ` · ${req.section_title}` : ""}
            </div>
          )}
          <button onClick={openMaterial} className="mt-2 text-xs uppercase tracking-widest text-espresso/80 hover:text-espresso" data-testid="staff-request-open-material">
            Открыть материал →
          </button>
        </div>
        <div className="border-t hairline pt-3 flex flex-wrap gap-2 items-center">
          {!req.assigned_staff_user_id && !isClosed && (
            <Button onClick={take} data-testid="staff-request-take"
              className="btn-pill min-h-[36px] bg-[#F2EADF] hover:bg-[#EADFCF] text-espresso border border-espresso/25 shadow-none font-medium">
              Взять в работу
            </Button>
          )}
          {req.assigned_staff_user_id && (
            <span className="text-xs text-muted-foreground">В работе: <span className="text-espresso font-medium">{req.assigned_staff_name}</span></span>
          )}
          {!isClosed && (
            <>
              <Select value={req.status} onValueChange={changeStatus}>
                <SelectTrigger className="h-9 rounded-xl w-[200px]" data-testid="staff-request-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Новое</SelectItem>
                  <SelectItem value="in_progress">В работе</SelectItem>
                  <SelectItem value="waiting_for_user">Ждём пользователя</SelectItem>
                  <SelectItem value="closed">Закрыто</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => changeStatus("closed")} variant="outline" data-testid="staff-request-close"
                className="btn-pill min-h-[36px]">
                Закрыть
              </Button>
            </>
          )}
        </div>
      </div>

      <ul className="space-y-3" data-testid="staff-request-messages">
        {req.messages.map((m) => (
          <li key={m.id} className={`flex gap-3 ${m.sender_type === "staff" ? "justify-end" : ""}`}>
            {m.sender_type === "user" && <StaffAvatar src={m.sender_avatar} name={m.sender_name} />}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.sender_type === "staff" ? "bg-[#F2EADF] border border-[#E1D2B8] text-espresso" : "bg-white border hairline text-espresso"}`}>
              <div className="text-[11px] text-muted-foreground mb-0.5">
                <span className="font-medium text-espresso">{m.sender_name}</span>
                <span> · {new Date(m.created_at).toLocaleString("ru-RU")}</span>
              </div>
              <div className="font-serif whitespace-pre-wrap leading-snug">{m.body}</div>
            </div>
            {m.sender_type === "staff" && <StaffAvatar src={m.sender_avatar} name={m.sender_name} />}
          </li>
        ))}
      </ul>

      {isClosed ? (
        <div className="rounded-2xl bg-sand/40 border hairline p-3 text-sm text-muted-foreground text-center">
          Обращение закрыто.
        </div>
      ) : (
        <div className="rounded-2xl bg-white border hairline p-3">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Ответ</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
            placeholder="Ваш ответ пользователю…"
            data-testid="staff-reply-input"
            disabled={sending}
          />
          <div className="mt-2 flex justify-end">
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className="btn-pill min-h-[40px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none font-medium"
              data-testid="staff-reply-send"
            >
              {sending ? "Отправляем…" : "Ответить"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



// ---------- Staff Comments Panel (Sprint 40) ----------
function StaffCommentsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = status === "all" ? "" : `?status=${status}`;
      const { data } = await api.get(`/staff/comments${q}`);
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const hide = async (cid) => { try { await api.post(`/staff/comments/${cid}/hide`); toast.success("Скрыто"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const unhide = async (cid) => { try { await api.post(`/staff/comments/${cid}/unhide`); toast.success("Показано"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (cid) => { if (!window.confirm("Удалить комментарий?")) return; try { await api.delete(`/staff/comments/${cid}`); toast.success("Удалено"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3" data-testid="staff-comments-panel">
      <div className="flex flex-wrap gap-2">
        {[
          ["all", "Все"], ["visible", "Новые"], ["hidden", "Скрытые"], ["deleted", "Удалённые"],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setStatus(v)}
            data-testid={`staff-comments-filter-${v}`}
            className={`chip px-3 py-1 border text-xs uppercase tracking-widest transition ${
              status === v ? "bg-espresso text-white border-espresso" : "bg-white text-espresso border-espresso/25 hover:bg-sand/40"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border hairline px-6 py-10 text-center" data-testid="staff-comments-empty">
          <p className="font-serif italic text-espresso/70 text-lg">Комментариев нет</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="staff-comments-list">
          {items.map((c) => (
            <li key={c.id} className="rounded-2xl bg-white border hairline p-4" data-testid={`staff-comment-item-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-sand/50 overflow-hidden border hairline flex items-center justify-center text-espresso/60 font-serif shrink-0" style={{ width: 40, height: 40 }}>
                  {c.author_avatar ? <img src={c.author_avatar} alt="" className="w-full h-full object-cover" /> : (c.author_name || "?").trim().slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-espresso truncate">{c.author_name || "—"}</span>
                    {c.author_is_staff && <span className="text-[10px] uppercase tracking-widest text-espresso/70">Команда Anokina Studio</span>}
                    <span className="text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleString("ru-RU")}</span>
                    {c.status === "hidden" && <span className="text-[10px] uppercase tracking-widest text-[#8A4D14]">скрыто</span>}
                    {c.status === "deleted" && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">удалено</span>}
                    {c.parent_comment_id && <span className="text-[10px] uppercase tracking-widest text-espresso/60">ответ</span>}
                  </div>
                  <div className="text-sm text-espresso mt-0.5">«{c.material_title || "—"}»</div>
                  <div className="text-sm text-espresso mt-1 font-serif whitespace-pre-wrap line-clamp-3">{c.body || "—"}</div>
                  <div className="mt-2 flex gap-3 flex-wrap text-[11px] uppercase tracking-widest">
                    {c.material_slug && (
                      <a href={`/library/${encodeURIComponent(c.material_slug)}#comment-${c.id}`} target="_blank" rel="noreferrer" className="text-espresso/70 hover:text-espresso" data-testid={`staff-comment-open-${c.id}`}>
                        Открыть материал
                      </a>
                    )}
                    {c.status === "visible" && (
                      <button onClick={() => hide(c.id)} className="text-espresso/70 hover:text-espresso" data-testid={`staff-comment-hide-${c.id}`}>Скрыть</button>
                    )}
                    {c.status === "hidden" && (
                      <button onClick={() => unhide(c.id)} className="text-espresso/70 hover:text-espresso" data-testid={`staff-comment-unhide-${c.id}`}>Показать</button>
                    )}
                    {c.status !== "deleted" && (
                      <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-red-500" data-testid={`staff-comment-delete-${c.id}`}>Удалить</button>
                    )}
                  </div>
                </div>
                {(c.replies_count || 0) > 0 && (
                  <span className="text-[10px] uppercase tracking-widest text-espresso/70 shrink-0">{c.replies_count} отв.</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



// ---------- Analytics Panel (Sprint 44) ----------
const ANALYTICS_METRICS = [
  { key: "views",            label: "Просмотры" },
  { key: "unique_viewers",   label: "Уникальные" },
  { key: "unique_depth_75",  label: "Дочитали до 75%" },
  { key: "saves",            label: "Сохранения" },
  { key: "likes",            label: "Понравилось" },
  { key: "comments",         label: "Комментарии" },
  { key: "private_requests", label: "Обращения" },
];

function fmtPct(v) { return v == null ? "—" : `${v}%`; }

function AnalyticsPanel() {
  const { role } = useAdminNav();
  const [area, setArea] = useState("materials");
  return (
    <div className="space-y-4" data-testid="analytics-root">
      <div className="inline-flex rounded-full bg-sand/40 p-1 gap-1">
        <button
          type="button"
          onClick={() => setArea("materials")}
          className="h-9 px-4 rounded-xl text-[12.5px]"
          style={{
            background: area === "materials" ? "#4A3728" : "transparent",
            color: area === "materials" ? "#F7EFE0" : "#5B4326",
          }}
          data-testid="analytics-area-materials"
        >
          Материалы
        </button>
        {(role === "owner" || role === "administrator") && <button
          type="button"
          onClick={() => setArea("master-order")}
          className="h-9 px-4 rounded-xl text-[12.5px]"
          style={{
            background: area === "master-order" ? "#4A3728" : "transparent",
            color: area === "master-order" ? "#F7EFE0" : "#5B4326",
          }}
          data-testid="analytics-area-master-order"
        >
          Мастер заказа
        </button>}
      </div>
      {area === "master-order" ? <MasterOrderAnalyticsPanel /> : <MaterialsAnalyticsPanel />}
    </div>
  );
}

function MaterialsAnalyticsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("views");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/analytics/materials");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (openId) {
    return <AnalyticsMaterialDetail rid={openId} onBack={() => {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }} />;
  }

  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    return sortDir === "desc" ? bv - av : av - bv;
  });

  const openDetail = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("open", id);
    setSearchParams(next, { replace: true });
  };

  const setSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) {
    return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  }
  if (rows.length === 0) {
    return <div className="rounded-3xl bg-white border hairline px-6 py-10 text-center">
      <p className="font-serif italic text-espresso/70 text-lg">Опубликованных материалов пока нет</p>
    </div>;
  }

  return (
    <div data-testid="analytics-panel">
      {/* Desktop table */}
      <div className="hidden md:block overflow-hidden rounded-2xl border hairline bg-white">
        <table className="w-full text-sm">
          <thead className="bg-cream/50 text-espresso">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest">Материал</th>
              {ANALYTICS_METRICS.map((m) => (
                <th key={m.key} className="text-right px-3 py-2 text-[11px] uppercase tracking-widest cursor-pointer select-none whitespace-nowrap"
                    onClick={() => setSort(m.key)}
                    data-testid={`analytics-sort-${m.key}`}>
                  {m.label}{sortKey === m.key ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id} className="border-t hairline hover:bg-sand/20 cursor-pointer" onClick={() => openDetail(r.id)} data-testid={`analytics-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-espresso truncate max-w-[280px]" title={r.title}>{r.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[280px]">
                    {r.product_title || "Открытая библиотека"}{r.section_title ? ` · ${r.section_title}` : ""}
                  </div>
                </td>
                {ANALYTICS_METRICS.map((m) => (
                  <td key={m.key} className="text-right px-3 py-2 text-espresso tabular-nums">{r[m.key] ?? 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden space-y-2" data-testid="analytics-cards">
        {sorted.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => openDetail(r.id)}
              className="w-full text-left rounded-2xl bg-white border hairline p-4 hover:bg-sand/20"
              data-testid={`analytics-card-${r.id}`}
            >
              <div className="font-medium text-espresso">{r.title}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {r.product_title || "Открытая библиотека"}{r.section_title ? ` · ${r.section_title}` : ""}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                {ANALYTICS_METRICS.map((m) => (
                  <div key={m.key} className="flex justify-between">
                    <span className="text-muted-foreground">{m.label}</span>
                    <span className="text-espresso tabular-nums">{r[m.key] ?? 0}</span>
                  </div>
                ))}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatActiveSeconds(value) {
  const total = Math.max(0, Number(value) || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
}

function MasterOrderAnalyticsPanel() {
  const [period, setPeriod] = useState("30");
  const [audience, setAudience] = useState("all");
  const [summary, setSummary] = useState(null);
  const [testers, setTesters] = useState([]);
  const [testerId, setTesterId] = useState("");
  const [testerDetail, setTesterDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fromTs = useMemo(() => {
    const value = new Date();
    value.setUTCDate(value.getUTCDate() - Number(period));
    return value.toISOString();
  }, [period]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data }, applicationsResponse] = await Promise.all([
          api.get("/admin/analytics/master-order", {
            params: { from_ts: fromTs, audience },
          }),
          api.get("/admin/master-order-testing/applications"),
        ]);
        if (!cancelled) {
          setSummary(data);
          setTesters((applicationsResponse.data || []).filter((item) =>
            ["accepted", "active_test", "completed_test", "feedback_received"].includes(item.status)
          ));
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e) || "Не удалось загрузить аналитику");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fromTs, audience]);

  useEffect(() => {
    if (!testerId) {
      setTesterDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/analytics/master-order/testers/${testerId}`,
          { params: { from_ts: fromTs } }
        );
        if (!cancelled) setTesterDetail(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e) || "Не удалось загрузить данные участника");
      }
    })();
    return () => { cancelled = true; };
  }, [testerId, fromTs]);

  if (loading || !summary) {
    return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  }

  const totals = summary.totals || {};
  const kpis = [
    ["Пользователи", totals.users || 0],
    ["Сессии", totals.sessions || 0],
    ["Среднее активное время", formatActiveSeconds(totals.average_active_seconds)],
    ["Повторные посещения", totals.return_visits || 0],
  ];

  return (
    <div className="space-y-4" data-testid="master-order-analytics-panel">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="mo-analytics-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Последние 7 дней</SelectItem>
            <SelectItem value="30">Последние 30 дней</SelectItem>
            <SelectItem value="90">Последние 90 дней</SelectItem>
          </SelectContent>
        </Select>
        <Select value={audience} onValueChange={setAudience}>
          <SelectTrigger className="w-full sm:w-[230px]" data-testid="mo-analytics-audience">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все пользователи</SelectItem>
            <SelectItem value="testers">Участники тестирования</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {kpis.map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-white border hairline p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="font-serif text-xl text-espresso mt-1">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <AnalyticsSimpleList
          title="Востребованные разделы"
          rows={summary.top_sections}
          label={(row) => row.section}
        />
        <AnalyticsSimpleList
          title="Востребованные инструменты"
          rows={summary.top_tools}
          label={(row) => row.tool}
        />
        <AnalyticsSimpleList
          title="Частые переходы"
          rows={summary.transitions}
          label={(row) => `${row.from} → ${row.to}`}
        />
        <AnalyticsSimpleList
          title="Места прекращения работы"
          rows={summary.dropoffs}
          label={(row) => row.section}
        />
      </div>

      <AnalyticsSimpleList
        title="Технические ошибки"
        rows={summary.errors}
        label={(row) => `${row.section || "раздел не указан"} · ${row.code}`}
      />

      <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-espresso">
          Участник закрытого тестирования
        </div>
        <Select value={testerId || "__none__"} onValueChange={(value) => setTesterId(value === "__none__" ? "" : value)}>
          <SelectTrigger className="w-full" data-testid="mo-analytics-tester">
            <SelectValue placeholder="Выберите участника" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Не выбран</SelectItem>
            {testers.map((item) => (
              <SelectItem key={item.user_id} value={item.user_id}>
                {item.user_name_snapshot || item.user_email || "Участник"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {testerDetail && (
          <div className="grid sm:grid-cols-2 gap-2 text-sm" data-testid="mo-analytics-tester-detail">
            <div>Начало: {formatDate(testerDetail.access_started_at)}</div>
            <div>Окончание: {formatDate(testerDetail.access_expires_at)}</div>
            <div>Последний визит: {formatDate(testerDetail.last_visit_at)}</div>
            <div>Активное время: {formatActiveSeconds(testerDetail.active_seconds)}</div>
            <div className="sm:col-span-2">
              Разделы: {(testerDetail.sections || []).join(", ") || "—"}
            </div>
            <div className="sm:col-span-2">
              Функции: {(testerDetail.tools || []).join(", ") || "—"}
            </div>
            <div className="sm:col-span-2">
              Ошибки: {(testerDetail.errors || []).map((item) =>
                `${item.section || "—"} · ${item.code} (${item.count})`
              ).join(", ") || "—"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AnalyticsSimpleList({ title, rows = [], label }) {
  return (
    <div className="rounded-2xl bg-white border hairline p-4">
      <div className="text-[10px] uppercase tracking-widest text-espresso mb-2">{title}</div>
      {rows.length > 0 ? (
        <ul className="space-y-1.5 text-sm">
          {rows.map((row, index) => (
            <li key={`${label(row)}-${index}`} className="flex justify-between gap-3">
              <span className="text-espresso truncate">{label(row)}</span>
              <span className="text-muted-foreground tabular-nums">{row.count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground">Пока нет данных</div>
      )}
    </div>
  );
}

function AnalyticsMaterialDetail({ rid, onBack }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/staff/analytics/materials/${rid}`); setD(data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [rid]);
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (!d) return <div className="rounded-3xl bg-white border hairline p-6 text-center">Данных нет</div>;
  const noData = (d.views || 0) === 0;
  return (
    <div className="space-y-4" data-testid="analytics-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onBack} className="chip bg-white border border-espresso/25 text-espresso px-3 py-1.5 hover:bg-sand/40 inline-flex items-center gap-1.5" data-testid="analytics-detail-back">
          <ChevronLeft className="w-3.5 h-3.5" /> К списку
        </button>
        {d.slug && (
          <a href={`/library/${encodeURIComponent(d.slug)}`} target="_blank" rel="noreferrer"
             className="text-[11px] uppercase tracking-widest text-espresso/80 hover:text-espresso" data-testid="analytics-open-material">
            Открыть материал →
          </a>
        )}
      </div>

      <div className="rounded-3xl bg-white border hairline p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Материал</div>
        <div className="font-serif text-xl text-espresso mt-0.5" data-testid="analytics-detail-title">{d.title || "—"}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {d.product_title || "Открытая библиотека"}{d.section_title ? ` · ${d.section_title}` : ""}
        </div>
      </div>

      {noData ? (
        <div className="rounded-3xl bg-white border hairline p-6 text-center text-sm text-muted-foreground font-serif italic" data-testid="analytics-empty">
          Данных о просмотрах пока нет.
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ANALYTICS_METRICS.map((m) => (
          <div key={m.key} className="rounded-2xl bg-white border hairline p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</div>
            <div className="font-serif text-2xl text-espresso mt-1 tabular-nums" data-testid={`analytics-metric-${m.key}`}>{d[m.key] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-cream/60 border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Дочитывание</div>
          <div className="font-serif text-2xl text-espresso mt-1 tabular-nums" data-testid="analytics-finish-rate">{fmtPct(d.finish_rate)}</div>
        </div>
        <div className="rounded-2xl bg-cream/60 border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Сохранения от читателей</div>
          <div className="font-serif text-2xl text-espresso mt-1 tabular-nums" data-testid="analytics-save-rate">{fmtPct(d.save_rate)}</div>
        </div>
      </div>
    </div>
  );
}



function InstructionsPanel() {
  const { pendingAction, clearPendingAction, pendingInstructionId, clearPendingInstructionId } = useAdminNav();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [creating, setCreating] = useState(false);

  // Overview → Quick Action "Создать инструкцию".
  useEffect(() => {
    if (pendingAction?.tab === "instructions" && pendingAction?.kind === "create") {
      setCreating(true);
      clearPendingAction();
    }
  }, [pendingAction, clearPendingAction]);

  // Overview → Recent Activity click on an instruction opens the view dialog.
  useEffect(() => {
    if (!pendingInstructionId || items.length === 0) return;
    const it = items.find((x) => x.id === pendingInstructionId);
    if (it) {
      setViewing(it);
      clearPendingInstructionId();
    }
  }, [pendingInstructionId, items, clearPendingInstructionId]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (category && category !== "all") params.set("category", category);
      const qs = params.toString();
      const url = qs ? `/admin/instructions/search?${qs}` : "/admin/instructions";
      const { data } = await api.get(url);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, category]); // eslint-disable-line

  const create = async (data) => {
    try {
      const { data: created } = await api.post("/admin/instructions", data);
      toast.success("Создано");
      setCreating(false);
      setEditing(created);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const save = async (data) => {
    const { id, created_at, updated_at, ...body } = data;
    try {
      await api.patch(`/admin/instructions/${id}`, body);
      toast.success("Сохранено");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Удалить инструкцию?")) return;
    try {
      await api.delete(`/admin/instructions/${id}`);
      toast.success("Удалено");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = items.filter((i) => statusFilter === "all" || i.status === statusFilter);
  // Group by category, sorted by sort_order then title.
  const grouped = filtered.reduce((acc, i) => {
    (acc[i.category] ||= []).push(i);
    return acc;
  }, {});
  const categoriesInOrder = [
    ...INSTRUCTION_CATEGORIES.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !INSTRUCTION_CATEGORIES.includes(c)).sort(),
  ];

  return (
    <div data-testid="admin-instructions" className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Поиск по заголовку, содержанию, категории"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-9 rounded-xl h-10 bg-white" data-testid="instructions-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-10 rounded-xl bg-white sm:w-56" data-testid="instructions-filter-category">
            <SelectValue placeholder="Категория" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все категории</SelectItem>
            {INSTRUCTION_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 rounded-xl bg-white sm:w-44" data-testid="instructions-filter-status">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="draft">Черновик</SelectItem>
            <SelectItem value="published">Опубликовано</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => setCreating(true)}
          className="btn-pill bg-espresso hover:bg-espressoDark text-white h-auto py-2 px-4 text-sm"
          data-testid="instructions-new"
        >
          <Plus className="w-4 h-4 mr-1" /> Новая инструкция
        </Button>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="instructions-count">
        Найдено: {filtered.length}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground font-serif italic text-center py-8">
          Инструкций пока нет.
        </p>
      ) : (
        <div className="space-y-4">
          {categoriesInOrder.map((cat) => (
            <div key={cat} className="rounded-2xl border hairline bg-white" data-testid={`instructions-category-${cat}`}>
              <div className="px-4 py-2 border-b hairline bg-sand/30">
                <span className="text-xs uppercase tracking-widest text-espresso">{cat}</span>
                <span className="text-[10px] text-muted-foreground ml-2">· {grouped[cat].length}</span>
              </div>
              <ul>
                {grouped[cat]
                  .slice()
                  .sort((a, b) => (a.sort_order - b.sort_order) || a.title.localeCompare(b.title, "ru"))
                  .map((it) => (
                    <li key={it.id} className="flex items-center gap-2 px-4 py-3 border-b last:border-b-0 hairline hover:bg-sand/10"
                        data-testid={`instructions-item-${it.id}`}>
                      <button
                        onClick={() => setViewing(it)}
                        className="flex-1 text-left"
                        data-testid={`instructions-open-${it.id}`}
                      >
                        <div className="text-sm text-espresso">{it.title}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {it.status === "published" ? "Опубликовано" : "Черновик"}
                        </div>
                      </button>
                      <span className={`chip text-[10px] border ${it.status === "published" ? "bg-emerald-100 text-emerald-900 border-emerald-200" : "bg-amber-100 text-amber-900 border-amber-200"}`}>
                        {it.status === "published" ? "Опубл." : "Черновик"}
                      </span>
                      <button
                        onClick={() => setEditing(it)}
                        className="p-1 rounded-full hover:bg-sand/50"
                        title="Редактировать"
                        data-testid={`instructions-edit-${it.id}`}
                      ><Pencil className="w-4 h-4 text-espresso" /></button>
                      <button
                        onClick={() => remove(it.id)}
                        className="p-1 rounded-full hover:bg-red-50"
                        title="Удалить"
                        data-testid={`instructions-delete-${it.id}`}
                      ><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <InstructionEditDialog
        open={!!editing || creating}
        data={editing}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSave={editing ? save : create}
      />
      <InstructionViewDialog
        open={!!viewing}
        data={viewing}
        onClose={() => setViewing(null)}
        onEdit={() => { setEditing(viewing); setViewing(null); }}
      />
    </div>
  );
}

function InstructionEditDialog({ open, data, onClose, onSave }) {
  const isNew = !data?.id;
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(data ? { ...data } : {
      title: "", category: INSTRUCTION_CATEGORIES[0], content: "", status: "draft", sort_order: 100,
    });
  }, [data, open]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => {
    if (!form.title || !form.title.trim()) {
      toast.error("Введите заголовок");
      return;
    }
    if (!form.category || !form.category.trim()) {
      toast.error("Выберите категорию");
      return;
    }
    onSave({
      ...form,
      title: form.title.trim(),
      category: form.category.trim(),
      sort_order: Number.isFinite(+form.sort_order) ? +form.sort_order : 0,
    });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="instructions-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-espresso">
            {isNew ? "Новая инструкция" : "Инструкция"}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Внутренняя инструкция для команды Anokina Studio. Задайте категорию, статус и содержимое.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Заголовок" v={form.title} onChange={(v) => set("title", v)} testid="instructions-title" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectField
              label="Категория"
              v={form.category || ""}
              onChange={(v) => set("category", v)}
              testid="instructions-category"
              options={INSTRUCTION_CATEGORIES.map((c) => [c, c])}
            />
            <SelectField
              label="Статус"
              v={form.status || "draft"}
              onChange={(v) => set("status", v)}
              testid="instructions-status"
              options={[["draft", "Черновик"], ["published", "Опубликовано"]]}
            />
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Порядок</label>
              <Input
                type="number"
                value={form.sort_order ?? 0}
                onChange={(e) => set("sort_order", parseInt(e.target.value || "0", 10) || 0)}
                className="mt-1 h-10 rounded-xl"
                data-testid="instructions-sort"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Содержание</label>
            <div className="mt-1" data-testid="instructions-content">
              <RichEditor
                value={form.content || ""}
                onChange={(v) => set("content", v)}
                placeholder="Опишите шаги, правила или примеры…"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Отмена</Button>
          <Button onClick={submit} className="btn-pill bg-espresso text-white" data-testid="instructions-save">
            {isNew ? "Создать" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstructionViewDialog({ open, data, onClose, onEdit }) {
  if (!open || !data) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="instructions-view-dialog">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{data.category}</div>
          <DialogTitle className="font-serif text-2xl text-espresso">{data.title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Просмотр инструкции. Для правки нажмите «Редактировать».
          </DialogDescription>
        </DialogHeader>
        <article
          className="anokina-content text-espresso"
          data-testid="instructions-view-content"
          dangerouslySetInnerHTML={{ __html: data.content || "<p><em>Пусто</em></p>" }}
        />
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Закрыть</Button>
          <Button onClick={onEdit} className="btn-pill bg-espresso text-white" data-testid="instructions-view-edit">
            <Pencil className="w-4 h-4 mr-1" /> Редактировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Дополнительные настройки (collapsible) ----------
function AdvancedSettings({ sortOrder, onSortOrderChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border hairline bg-white/60" data-testid="material-advanced-settings">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="material-advanced-toggle"
        aria-expanded={open}
      >
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Дополнительные настройки</span>
        {open ? <ChevronUp className="w-4 h-4 text-espresso" /> : <ChevronDown className="w-4 h-4 text-espresso" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid="material-advanced-body">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Порядок сортировки</label>
            <Input
              value={sortOrder ?? ""}
              onChange={(e) => onSortOrderChange(parseInt(e.target.value || "0", 10) || 0)}
              className="mt-1 rounded-xl h-11 max-w-[180px]"
              data-testid="material-sort"
              type="number"
              inputMode="numeric"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Число: чем меньше, тем выше в списках. Обычно не требуется.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
